import { useMutation } from "@tanstack/react-query";
import {
  ArrowRight,
  Check,
  ChevronDown,
  ChevronUp,
  Menu,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useStartEvaluation } from "@/hooks/useStartEvaluation";
import { useSessionSwitcher } from "@/contexts/SessionSwitcherContext";
import { useToast } from "@/hooks/use-toast";
import type { EntityData } from "@/types/entity";
import { findDuplicateCuiSession, isSessionEmpty } from "@/utils/multi-session";
import { saveUnauthenticatedEntityData } from "@/utils/localStorageEntityData";
import { SharedFooter } from "@/components/common/SharedFooter";
import { CuiVerificationSection } from "./LandingAlternativeV40/CuiVerificationSection";

const benefitCards = [
  {
    num: "01",
    tag: "Timp economisit",
    title: "Evaluare NIS2 in 39 de minute, nu in zile",
    body: "In loc sa sari intre fisiere Excel DNSC, corelari CAEN si calcule manuale, NIS2PASS ruleaza fluxul complet de evaluare initiala intr-o singura sesiune ghidata.",
    stats: [
      { val: "~39 min", label: "pentru evaluarea initiala completa" },
      { val: "3 fluxuri", label: "notificare, impact, ENIRE@RO" },
      { val: "0 copieri", label: "intre sabloane si documente separate" },
    ],
  },
  {
    num: "02",
    tag: "Precizie calcul",
    title: "Zero erori in calculele DNSC obligatorii",
    body: "Algoritmii NIS2PASS sunt sincronizati cu metodologiile oficiale DNSC: corelarea codurilor CAEN cu sectoarele NIS2, calculele de risc ENIRE@RO si pragurile de clasificare entitati esentiale/importante — totul automatizat.",
    stats: [
      { val: "217+", label: "coduri CAEN mapate automatizat" },
      { val: "100%", label: "aliniere metodologie DNSC" },
      { val: "0 manual", label: "calcule sau corelari manuale" },
    ],
  },
  {
    num: "03",
    tag: "Audit-ready",
    title: "Documentatie NIS2 gata de audit si transmitere",
    body: "Fiecare document generat — Formular Notificare, Raport Risc, EVAL_MMS CyFun — urmeaza formatul oficial solicitat de DNSC, gata de semnat si transmis pe platforma NIS2@RO.",
    stats: [
      { val: "4 doc", label: "generate automatizat per evaluare" },
      { val: "PDF+Excel", label: "formate oficiale DNSC" },
      { val: "1 click", label: "descarcare dosar complet" },
    ],
  },
  {
    num: "04",
    tag: "Vizibilitate CISO",
    title: "Dashboard CISO cu scoruri CyFun in timp real",
    body: "Dashboard centralizat cu scorurile de maturitate pe cele 6 functii CyFun (Identificare, Protejare, Detectare, Raspuns, Recuperare, Guvernanta), gap-uri identificate si stadiul obligatiilor conform OUG 155/2024.",
    stats: [
      { val: "6 functii", label: "CyFun monitorizate in timp real" },
      { val: "1-5", label: "scala maturitate MMS DNSC" },
      { val: "Gap-uri", label: "identificate automatizat per functie" },
    ],
  },
  {
    num: "05",
    tag: "Ghidare pas cu pas",
    title: "Ghidare educativa pe fiecare cerinta NIS2",
    body: "Platforma explica fiecare cerinta din Directiva 2022/2555 si OUG 155/2024: de ce este necesara, ce articol o impune, ce dovezi trebuie colectate pentru audit si ce nivel de maturitate CyFun este vizat.",
    stats: [
      { val: "218", label: "cerinte CyFun explicate" },
      { val: "Context", label: "legal pe fiecare articol" },
      { val: "Dovezi", label: "necesare listate per cerinta" },
    ],
  },
  {
    num: "06",
    tag: "Compatibilitate NIS2@RO",
    title: "Compatibilitate completa cu platforma oficiala NIS2@RO",
    body: "NIS2PASS nu inlocuieste platforma oficiala NIS2@RO a DNSC — o completeaza. Documentele generate sunt 100% compatibile cu procedurile de notificare, evaluare si raportare cerute de OUG 155/2024 si Legea 124/2025.",
    stats: [
      { val: "100%", label: "compatibil format DNSC" },
      { val: "NIS2@RO", label: "documente transmisibile direct" },
      { val: "2 legi", label: "OUG 155 + Legea 124" },
    ],
  },
];

const faqItems = [
  {
    q: "NIS2PASS inlocuieste platforma oficiala NIS2@RO de la DNSC?",
    a: "Nu. NIS2PASS este un instrument auxiliar de conformare, nu o platforma oficiala. Platforma statului (NIS2@RO) operata de DNSC ramane canalul oficial de inregistrare si comunicare. NIS2PASS pregateste documentele in formatul cerut de DNSC pentru a fi transmise prin NIS2@RO.",
  },
  {
    q: "Cine ia decizia finala de clasificare NIS2 — esentiala sau importanta?",
    a: "Exclusiv DNSC (Directoratul National de Securitate Cibernetica), conform art. 18 alin. (4-5) din OUG 155/2024. NIS2PASS ofera o evaluare preliminara bazata pe criteriile legale.",
  },
  {
    q: "Ce include contul gratuit NIS2PASS cu 100 credite?",
    a: "Contul gratuit include parcurgerea integrala a fluxului de evaluare: identificare aplicabilitate NIS2, analiza efect perturbator, evaluare nivel risc conform ENIRE@RO si autoevaluare maturitate CyFun. Descarcarea documentelor PDF si Excel necesita credite suplimentare.",
  },
  {
    q: "Cat dureaza evaluarea conformitatii NIS2 pe platforma?",
    a: "Fluxul complet de evaluare NIS2 dureaza in medie ~39 de minute. Platforma pre-completeaza datele companiei din surse publice (ONRC) si ofera ghidare contextuala la fiecare pas.",
  },
  {
    q: "Cum sunt protejate datele companiei pe NIS2PASS?",
    a: "Datele sunt stocate securizat conform GDPR. Nu partajam date cu terti si nu transmitem informatii catre DNSC fara actiunea ta explicita. Poti solicita stergerea datelor oricand.",
  },
  {
    q: "Formularele CyFun din NIS2PASS sunt cele oficiale DNSC?",
    a: "Formularele EVAL_MMS sunt implementate conform versiunilor publicate de DNSC. Platforma se actualizeaza automatizat la publicarea versiunilor oficiale finale prin Ordinul DNSC.",
  },
  {
    q: "Care sunt sanctiunile pentru neconformare cu NIS2 in Romania?",
    a: "Conform art. 60-63 din OUG 155/2024, sanctiunile pentru neindeplinirea obligatiilor NIS2 pot ajunge la 500.000 lei per obligatie. Termenele de conformare incep de la data comunicarii deciziei DNSC (T0).",
  },
];

const cycleLevels = [
  {
    n: "NIVEL 1",
    title: "Companie",
    desc: "Date de identificare: CUI, cod CAEN Rev.2, tip entitate NIS2",
    color: "#60A5FA",
  },
  {
    n: "NIVEL 2",
    title: "Identificare NIS2",
    desc: "Verificare eligibilitate NIS2 si clasificare entitate esentiala sau importanta",
    color: "#60A5FA",
  },
  {
    n: "NIVEL 2a",
    title: "Analiza efect perturbator",
    desc: "Completare asistata a ghidului pentru realizarea analizei privind efectul perturbator",
    color: "#60A5FA",
    conditional: true,
  },
  {
    n: "NIVEL 3",
    title: "Decizie de Inregistrare",
    desc: "Pe baza analizei DNSC a formularului notificare entitatea este inregistrata",
    color: "#60A5FA",
  },
  {
    n: "NIVEL 4",
    title: "Analiza de Risc",
    desc: "Evaluare nivel de asigurare (nivel de risc) aplicand metodologia ENIRE",
    color: "#F59E0B",
  },
  {
    n: "NIVEL 5",
    title: "Evaluare Maturitate",
    desc: "Autoevaluare maturitate CyFun — scorare pe scala MMS DNSC",
    color: "#F59E0B",
  },
  {
    n: "NIVEL 6",
    title: "Implementare CyFun",
    desc: "Implementare masuri securitate cibernetica pe cele 6 functii CyFun",
    color: "#10B981",
  },
  {
    n: "NIVEL 7",
    title: "Audit si Conformitate",
    desc: "Pregatire dosar complet audit NIS2 — documente conformitate DNSC",
    color: "#10B981",
  },
];

const modules = [
  {
    num: "01",
    title: "Identificare si Notificare NIS2",
    desc: "Verificare automatizata CUI si cod CAEN Rev.2, identificare tip entitate NIS2 (esentiala sau importanta), generare formular notificare NIS2 conform cerintelor DNSC.",
    note: "Sincronizat cu baza de date ONRC si nomenclatorul CAEN Rev.2",
    docTitle: "PDF Formular Notificare NIS2",
    docDesc: "Document complet pentru inregistrare — format oficial DNSC",
  },
  {
    num: "02",
    title: "Evaluarea Efectului Perturbator",
    desc: "Analiza completa de impact conform Art. 9 — evaluare efect perturbator asupra serviciilor esentiale si importante, cu calcul automatizat al scorului de risc.",
    note: "Metodologie conforma cu ghidul DNSC de evaluare a impactului",
    docTitle: "PDF Raport Evaluare Efect Perturbator",
    docDesc:
      "Document oficial conform metodologiei DNSC — scor efect perturbator",
  },
  {
    num: "03",
    title: "Evaluarea Nivelului de Risc al Entitatii",
    desc: "Clasificare risc ENIRE@RO cu metodologia oficiala DNSC — determinare nivel entitate: De Baza, Importanta sau Esentiala, pe baza evaluarii complete de risc.",
    note: "Implementare completa a metodologiei clasificare risc ENIRE@RO publicata de DNSC",
    docTitle: "PDF Clasificare Risc ENIRE@RO",
    docDesc: "Nivel De Baza / Important / Esential — metodologie oficiala DNSC",
  },
  {
    num: "04",
    title: "Autoevaluarea Nivelului de Maturitate",
    desc: "Autoevaluare maturitate CyFun completa pe cele 6 functii: Identificare, Protejare, Detectare, Raspuns, Recuperare si Guvernanta — scorare automatizata pe scala MMS 1-5.",
    note: "Scorare automatizata pe scala 1-5 conform metodologiei MMS DNSC — 6 functii CyFun",
    docTitle: "Excel Autoevaluare Maturitate CyFun",
    docDesc: "Format oficial DNSC cu scoruri pe 6 functii — scala MMS 1-5",
  },
];

const LandingAlternativeV40 = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { toast } = useToast();
  const isLoggedIn = !!user;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [benefitSlide, setBenefitSlide] = useState(0);
  const [contactTopic, setContactTopic] = useState("");
  const [navScrolled, setNavScrolled] = useState(false);
  const [activeNavSection, setActiveNavSection] = useState("");
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [metricAnimated, setMetricAnimated] = useState(false);
  const [metric1, setMetric1] = useState(0);
  const [metric2, setMetric2] = useState(0);
  const [emailValue, setEmailValue] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [sliderPaused, setSliderPaused] = useState(false);
  const [obligTab, setObligTab] = useState(0);
  const benefitsRef = useRef<HTMLElement | null>(null);
  const metricsRef = useRef<HTMLDivElement | null>(null);
  const touchStartX = useRef(0);
  const sliderInteractionTimer = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const mobileMenuRef = useRef<HTMLDivElement | null>(null);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const scrollRafId = useRef<number>(0);

  const startEvaluation = useStartEvaluation();

  const {
    sessions: userSessions,
    archivedSessions,
    switchToSession,
    createSession,
  } = useSessionSwitcher();

  const { mutate: addCompanyWithCui, isPending: isCreatingSession } =
    useMutation({
      mutationFn: async (_cui: string) => {
        const success = await createSession();
        if (!success) throw new Error("Failed to create session");
      },
      onSuccess: (_data, cui) => {
        navigate(`/identificare-raportare?cui=${encodeURIComponent(cui)}`);
      },
      onError: () => {
        toast({
          title: "Eroare",
          description: "Nu s-a putut crea sesiunea. Incearca din nou.",
          variant: "destructive",
        });
      },
    });

  const handleCreateAccountWithCui = (cui: string) => {
    if (!isLoggedIn) {
      saveUnauthenticatedEntityData(
        { cui, nume_entitate: "" } as Partial<EntityData> as EntityData,
        [],
        0
      );
      navigate("/auth");
      return;
    }

    const match = findDuplicateCuiSession({
      cui,
      sessions: userSessions ?? [],
      archivedSessions: archivedSessions ?? [],
    });

    if (match?.isArchived) {
      toast({
        title: "Companie arhivată",
        description:
          "Această companie este arhivată. Dezarhiveaz-o din Companiile mele pentru a continua.",
        variant: "destructive",
      });
      return;
    }

    if (match) {
      switchToSession(match.session.id).then(() => {
        navigate("/identificare-raportare");
      });
      return;
    }

    const blankSession = userSessions?.find(isSessionEmpty);
    if (blankSession) {
      switchToSession(blankSession.id).then(() => {
        navigate(`/identificare-raportare?cui=${encodeURIComponent(cui)}`);
      });
      return;
    }

    addCompanyWithCui(cui);
  };

  const requestDemo = () => navigate("/contact");

  const sectionRefs = useRef<(HTMLElement | null)[]>([]);
  const setSectionRef = useCallback(
    (index: number) => (el: HTMLElement | null) => {
      sectionRefs.current[index] = el;
    },
    []
  );

  const staggerRefs = useRef<(HTMLElement | null)[]>([]);
  const setStaggerRef = useCallback(
    (index: number) => (el: HTMLElement | null) => {
      staggerRefs.current[index] = el;
    },
    []
  );

  useEffect(() => {
    const revealObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const target = entry.target as HTMLElement;
            if (target.dataset.revealType === "stagger") {
              target.classList.add("v40-stagger-visible");
            } else {
              target.classList.add("v40-section-visible");
            }
            revealObserver.unobserve(target);
          }
        }
      },
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
    );
    for (const el of sectionRefs.current) {
      if (el) revealObserver.observe(el);
    }
    for (const el of staggerRefs.current) {
      if (el) {
        el.dataset.revealType = "stagger";
        revealObserver.observe(el);
      }
    }
    return () => revealObserver.disconnect();
  }, []);

  // Sub-element progressive reveal observer
  useEffect(() => {
    const els = document.querySelectorAll(".v40-reveal");
    if (!els.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("v40-revealed");
            obs.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.05, rootMargin: "0px 0px 0px 0px" }
    );
    for (const el of els) obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (scrollRafId.current) return;
      scrollRafId.current = requestAnimationFrame(() => {
        const scrollY = window.scrollY;
        setNavScrolled(scrollY > 80);
        if (scrollY < 200) setActiveNavSection("");
        const docHeight =
          document.documentElement.scrollHeight - window.innerHeight;
        setScrollProgress(docHeight > 0 ? Math.min(scrollY / docHeight, 1) : 0);
        setShowBackToTop(scrollY > 600);
        scrollRafId.current = 0;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (scrollRafId.current) {
        cancelAnimationFrame(scrollRafId.current);
        scrollRafId.current = 0;
      }
    };
  }, []);

  const navSectionIds = [
    { id: "v40-sec-cycle", label: "Cum functioneaza" },
    { id: "v40-sec-modules", label: "Module" },
    { id: "v40-sec-pricing", label: "Preturi" },
    { id: "v40-sec-legal", label: "Cadru legal" },
    { id: "v40-sec-faq", label: "FAQ" },
    { id: "v40-sec-about", label: "Despre noi" },
  ];

  useEffect(() => {
    const sectionEls = navSectionIds
      .map((s) => document.getElementById(s.id))
      .filter(Boolean) as HTMLElement[];
    if (sectionEls.length === 0) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveNavSection(entry.target.id);
          }
        }
      },
      { threshold: 0.15, rootMargin: "-80px 0px -40% 0px" }
    );
    for (const el of sectionEls) obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const pauseSliderAutoAdvance = useCallback(() => {
    setSliderPaused(true);
    if (sliderInteractionTimer.current)
      clearTimeout(sliderInteractionTimer.current);
    sliderInteractionTimer.current = setTimeout(() => {
      setSliderPaused(false);
    }, 10000);
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const el = benefitsRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const inView = rect.top < window.innerHeight && rect.bottom > 0;
      if (!inView) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        pauseSliderAutoAdvance();
        setBenefitSlide(
          (s) => (s - 1 + benefitCards.length) % benefitCards.length
        );
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        pauseSliderAutoAdvance();
        setBenefitSlide((s) => (s + 1) % benefitCards.length);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [pauseSliderAutoAdvance]);

  useEffect(() => {
    if (sliderPaused) return;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) return;
    const interval = setInterval(() => {
      setBenefitSlide((s) => (s + 1) % benefitCards.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [sliderPaused]);

  useEffect(() => {
    return () => {
      if (sliderInteractionTimer.current)
        clearTimeout(sliderInteractionTimer.current);
    };
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const dx = e.changedTouches[0].clientX - touchStartX.current;
      if (Math.abs(dx) > 50) {
        pauseSliderAutoAdvance();
        if (dx < 0) {
          setBenefitSlide((s) => (s + 1) % benefitCards.length);
        } else {
          setBenefitSlide(
            (s) => (s - 1 + benefitCards.length) % benefitCards.length
          );
        }
      }
    },
    [pauseSliderAutoAdvance]
  );

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const el = metricsRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !metricAnimated) {
            setMetricAnimated(true);
            obs.unobserve(entry.target);
            const duration1 = 1500;
            const duration2 = 2000;
            const start = performance.now();
            const animate = (now: number) => {
              const elapsed = now - start;
              const p1 = Math.min(elapsed / duration1, 1);
              const p2 = Math.min(elapsed / duration2, 1);
              const ease1 = 1 - (1 - p1) ** 3;
              const ease2 = 1 - (1 - p2) ** 3;
              setMetric1(Math.round(ease1 * 39));
              setMetric2(Math.round(ease2 * 425));
              if (elapsed < Math.max(duration1, duration2)) {
                requestAnimationFrame(animate);
              } else {
                setMetric1(39);
                setMetric2(425);
              }
            };
            requestAnimationFrame(animate);
          }
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [metricAnimated]);

  useEffect(() => {
    if (!mobileMenuOpen) {
      document.body.classList.remove("v40-body-locked");
      return;
    }
    document.body.classList.add("v40-body-locked");
    const menuEl = mobileMenuRef.current;
    if (!menuEl) return;
    const focusableSelector =
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const firstFocusable = menuEl.querySelector<HTMLElement>(focusableSelector);
    firstFocusable?.focus();
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMobileMenuOpen(false);
        menuButtonRef.current?.focus();
        return;
      }
      if (e.key !== "Tab") return;
      const focusable = menuEl.querySelectorAll<HTMLElement>(focusableSelector);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.classList.remove("v40-body-locked");
    };
  }, [mobileMenuOpen]);

  const emailIsValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue);
  const showEmailError = emailTouched && emailValue.length > 0 && !emailIsValid;
  const showEmailSuccess =
    emailTouched && emailValue.length > 0 && emailIsValid;

  return (
    <div className="v40 font-mono bg-ds-surface-50 text-navy-900">
      <style>{`
        .v40 {
          --c-navy: #0F172A;
          --c-orange: #FF8400;
          --c-orange-hover: #FF9520;
          --c-black: #111;
          --c-white: #FFF;
          --c-blue: #2563EB;
          --c-blue-light: #60A5FA;
          --c-green: #22C55E;
          --c-border-dark: #CBD5E1;
        }
        .v40 * { box-sizing: border-box; margin: 0; padding: 0; }
        .v40 a, .v40 button { font-family: inherit; }
        .v40 button:focus-visible, .v40 input:focus-visible { outline: 2px solid var(--ds-blue-600); outline-offset: 2px; }

        /* ===== SELECTION STYLING ===== */
        .v40 ::selection { background: rgba(37,99,235,0.25); color: var(--ds-navy-900); }
        .v40-hero ::selection, .v40-benefits ::selection, .v40-final ::selection, .v40-cyfun ::selection, .v40-footer ::selection, .v40-cui-result ::selection { background: rgba(96,165,250,0.35); color: #FFF; }
        .v40-skip { position: absolute; left: -9999px; top: 0; z-index: 999; padding: 12px 24px; background: var(--ds-blue-600); color: #FFF; font-size: 14px; border-radius: 0 0 8px 0; }
        .v40-skip:focus { left: 0; }
        .v40-wrap { width: min(1296px, calc(100% - 144px)); margin: 0 auto; }
        .v40-sora { font-family: 'Sora', sans-serif; }
        .v40-eyebrow { font-size: 12px; letter-spacing: 1.4px; color: var(--c-blue); font-weight: 500; font-family: 'Space Mono', monospace; }
        .v40-h2 { font-family: 'Sora', sans-serif; font-size: clamp(32px, 4vw, 42px); font-weight: 700; letter-spacing: -1.3px; line-height: 1.08; color: var(--c-navy); }
        .v40-body { font-size: 14px; line-height: 1.45; color: var(--c-border-dark); }
        .v40-btn { border: none; border-radius: 999px; padding: 14px 28px; font-weight: 500; font-size: 14px; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: all 150ms ease; text-decoration: none; font-family: 'Space Mono', monospace; }
        .v40-btn-primary { background: var(--c-orange); color: var(--c-black); }
        .v40-btn-primary:hover { background: var(--c-orange-hover); transform: translateY(-1px); box-shadow: 0 4px 12px rgba(255,132,0,0.3); }
        .v40-hero-cta { background: rgb(22, 163, 74); color: #fff; }
        .v40-hero-cta:hover { background: rgb(34, 197, 94); box-shadow: 0 4px 12px rgba(34,197,94,0.3); }
        .v40-btn-ghost { background: transparent; color: #FFFFFF; border: 1px solid #60A5FA; }
        .v40-btn-ghost:hover { border-color: #93C5FD; color: #BFDBFE; }
        .v40-btn-secondary { background: #F2F3F0; color: var(--c-black); border: 1px solid #CBCCC9; }
        .v40-btn-secondary:hover { background: #E7E8E5; }

        /* ===== CUI PULSE ===== */
        @keyframes v40-pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(22,163,74,0.4); } 50% { box-shadow: 0 0 0 8px rgba(22,163,74,0); } }
        .v40-btn-pulse { animation: v40-pulse 2.5s ease-in-out infinite; }

        /* ===== CUI DEMO STATE ===== */
        .v40-cui-demo-label { display: inline-block; padding: 4px 10px; background: var(--ds-border-800); border-radius: 4px; margin-bottom: 8px; }

        /* ===== CUI ANIMATION ===== */
        .v40-cui-result-wrap { max-height: 0; opacity: 0; overflow: hidden; transition: max-height 0.6s cubic-bezier(0.4,0,0.2,1), opacity 0.5s ease 0.1s; }
        .v40-cui-result-wrap.revealed { max-height: 800px; opacity: 1; }
        .v40-cui-ctas-wrap { max-height: 0; opacity: 0; overflow: hidden; transition: max-height 0.4s ease 0.3s, opacity 0.4s ease 0.4s; }
        .v40-cui-ctas-wrap.revealed { max-height: 100px; opacity: 1; }

        /* ===== HERO ===== */
        .v40-hero { padding: 28px 0 48px; display: flex; flex-direction: column; }
        .v40-hero > * { max-width: 1200px; margin-left: auto; margin-right: auto; padding-left: 24px; padding-right: 24px; width: 100%; }
        .v40-hero .v40-hero-body { max-width: 1120px; }
        .v40-hero .v40-disclaimer { max-width: 1120px; }
        .v40-nav { display: flex; align-items: center; justify-content: space-between; margin-bottom: 28px; }
        .v40-logo { display: flex; align-items: center; gap: 8px; background: none; border: none; padding: 0; cursor: pointer; }
        .v40-logo-mark { width: 14px; height: 14px; border-radius: 2px; background: #22C55E; flex-shrink: 0; }
        .v40-logo-text { font-family: 'Sora', sans-serif; font-size: 18px; font-weight: 700; color: #fff; }
        .v40-nav-links { display: flex; align-items: center; gap: 24px; }
        .v40-nav-link { font-size: 12px; color: #CBD5E1; cursor: pointer; transition: color 150ms; background: none; border: none; padding: 0; font-family: inherit; }
        .v40-nav-link:hover { color: #FFFFFF; }
        .v40-nav-link:hover { color: #FFF; }
        .v40-nav-btns { display: flex; gap: 12px; align-items: center; }
        .v40-nav-ghost { background: transparent; color: #CBD5E1; font-size: 12px; border: 1px solid #334155; padding: 10px 18px; border-radius: 999px; cursor: pointer; font-family: inherit; }
        .v40-nav-cta { background: #FF8400; color: #111; font-size: 12px; font-weight: 500; border: none; padding: 10px 18px; border-radius: 999px; cursor: pointer; font-family: inherit; }
        .v40-hero-body { display: flex; gap: 48px; align-items: flex-start; padding: 32px 0 0; }
        .v40-hero-left { flex: 1; display: flex; flex-direction: column; gap: 20px; }
        .v40-hero-title { max-width: 640px; }
        .v40-hero-sub { max-width: 640px; }
        .v40-hero-right { width: 520px; flex-shrink: 0; background: var(--ds-navy-800); border: 1px solid var(--ds-border-700); border-radius: 12px; padding: 20px; position: relative; }
        .v40-hero-metrics { display: flex; gap: 48px; margin-bottom: 24px; }
        .v40-hero-metric-label { margin-top: 4px; }
        .v40-hero-tools { display: flex; flex-direction: column; gap: 12px; }
        .v40-hero-tool { display: flex; align-items: center; gap: 8px; }
        .v40-disclaimer { display: flex; align-items: center; gap: 16px; padding: 16px 18px; background: var(--ds-navy-950); border-radius: 10px; margin-top: 40px; }
        .v40-disclaimer-badge { display: flex; align-items: center; gap: 10px; padding: 8px 12px; background: var(--ds-border-800); border-radius: 999px; flex-shrink: 0; }
        .v40-disclaimer-dot { width: 10px; height: 10px; border-radius: 50%; }
        .v40-disclaimer-badge-text { white-space: nowrap; }
        .v40-disclaimer-text { line-height: 1.45; }

        /* ===== SECTION INNER WRAPPER ===== */
        .v40-inner { max-width: 1120px; margin: 0 auto; padding: 0 32px; }

        /* ===== CUI VERIFY ===== */
        .v40-cui { background: #FFF; padding: 96px 0; }
        .v40-cui-search { display: flex; align-items: center; gap: 12px; padding: 12px 16px; background: var(--ds-surface-100); border: 1px solid var(--ds-border-200); border-radius: 8px; max-width: 483px; margin-top: 24px; }
        .v40-cui-input { border: none; background: transparent; flex: 1; outline: none; }
        .v40-cui-input::placeholder { color: var(--ds-text-400); }
        .v40-cui-note { margin-top: 12px; }
        .v40-cui-demo-link { cursor: pointer; margin-left: 8px; transition: color 150ms; background: none; border: none; padding: 0; font-family: inherit; }
        .v40-cui-demo-link:hover { color: var(--ds-blue-700); text-decoration: underline; }
        .v40-cui-result { background: var(--ds-navy-900); border: 1px solid var(--ds-border-800); border-radius: 8px; padding: 24px 28px; margin-top: 24px; display: flex; flex-direction: column; gap: 16px; }
        .v40-cui-warn { padding: 8px 12px; background: var(--ds-border-800); border-radius: 4px; display: inline-block; }
        .v40-cui-company { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; }
        .v40-cui-labels { display: flex; gap: 16px; flex-wrap: wrap; }
        .v40-cui-label-group { display: flex; flex-direction: column; gap: 4px; }
        .v40-cui-label-title { text-transform: uppercase; }
        .v40-cui-grid { display: flex; gap: 56px; margin-top: 8px; }
        .v40-cui-col { flex: 1; display: flex; flex-direction: column; gap: 12px; }
        .v40-cui-card { padding: 14px 16px; background: var(--ds-navy-800); border-radius: 10px; display: flex; flex-direction: column; gap: 8px; }
        .v40-cui-card-body { line-height: 1.45; }
        .v40-cui-service { display: flex; flex-direction: column; gap: 2px; padding: 10px 14px; background: var(--ds-navy-800); border: 1px solid var(--ds-border-800); border-radius: 4px; }
        .v40-cui-ctas { display: flex; gap: 12px; margin-top: 16px; }

        /* ===== CUI SKELETON ===== */
        @keyframes v40-skeletonPulse {
          0% { opacity: 1; }
          50% { opacity: 0.4; }
          100% { opacity: 1; }
        }
        .v40-cui-skeleton { animation: v40-skeletonPulse 1s ease-in-out infinite; }
        .v40-cui-skeleton-bar { background: var(--ds-border-800); border-radius: 4px; }
        .v40-cui-skeleton-bar.warn { height: 32px; width: 100%; border-radius: 4px; }
        .v40-cui-skeleton-company { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-top: 14px; }
        .v40-cui-skeleton-bar.company-name { height: 22px; width: 55%; margin-top: 0; }
        .v40-cui-skeleton-bar.badge { height: 34px; width: 30%; margin-top: 0; }
        .v40-cui-skeleton-bar.info { height: 12px; width: 80%; margin-top: 10px; }
        .v40-cui-skeleton-bar.label { height: 12px; width: 40%; }
        .v40-cui-skeleton-grid { display: flex; gap: 56px; margin-top: 16px; }
        .v40-cui-skeleton-col { flex: 1; display: flex; flex-direction: column; gap: 10px; }
        .v40-cui-skeleton-card { background: var(--ds-navy-800); border-radius: 8px; }
        .v40-cui-skeleton-card.tall { height: 80px; }
        .v40-cui-skeleton-card.short { height: 52px; }
        .v40-cui-skeleton-service { height: 40px; background: var(--ds-navy-800); border: 1px solid var(--ds-border-800); border-radius: 4px; }
        .v40-cui-error { padding: 12px 16px; background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.3); border-radius: 8px; margin-bottom: 12px; }

        /* ===== CYCLE ===== */
        .v40-cycle { background: #FFF; padding: 96px 0; }
        .v40-cycle-body { display: flex; gap: 32px; margin-top: 28px; }
        .v40-cycle-left { flex: 1; background: var(--ds-navy-950); border: 1px solid var(--ds-border-700); border-radius: 12px; padding: 32px; display: flex; flex-direction: column; }
        .v40-cycle-level { display: flex; align-items: flex-start; gap: 16px; padding: 14px 0; }
        .v40-cycle-level + .v40-cycle-level { border-top: 1px solid var(--ds-border-800); }
        .v40-cycle-dot { width: 10px; height: 10px; border-radius: 50%; margin-top: 4px; flex-shrink: 0; }
        .v40-cycle-level-label { margin-bottom: 2px; }
        .v40-cycle-level-desc { margin-top: 2px; }
        .v40-cycle-right { width: 420px; flex-shrink: 0; display: flex; flex-direction: column; gap: 16px; }
        .v40-testimonial { padding: 28px; background: var(--ds-surface-100); border: 1px solid var(--ds-border-200); border-radius: 12px; display: flex; flex-direction: column; gap: 18px; }
        .v40-testimonial-quote { line-height: 1.6; }

        /* ===== OBLIGATIONS ===== */
        .v40-obligations { background: var(--ds-surface-100); padding: 96px 0; }
        .v40-tabs { display: flex; gap: 10px; margin-top: 28px; }
        .v40-tab { padding: 10px 20px; border-radius: 6px; cursor: pointer; font-family: inherit; }
        .v40-tab.active { background: var(--ds-navy-800); color: #FFF; }
        .v40-tab:not(.active) { background: #FFF; border: 1px solid var(--ds-border-300); color: var(--ds-border-700); }
        .v40-timeline { display: flex; flex-direction: column; gap: 8px; margin-top: 24px; }
        .v40-timeline-item { padding: 14px 18px; background: #FFF; border: 1px solid var(--ds-border-200); border-radius: 8px; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px; }
        .v40-timeline-step { }
        .v40-timeline-title { margin-top: 2px; }
        .v40-timeline-desc { margin-top: 2px; }
        .v40-timeline-tag { padding: 4px 10px; border-radius: 4px; }
        .v40-pain-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-top: 28px; }
        .v40-pain-card { background: var(--ds-navy-900); border: 1px solid var(--ds-border-800); border-radius: 10px; padding: 20px 24px; }
        .v40-pain-card-title { line-height: 1.25; }
        .v40-pain-card-body { line-height: 1.45; margin-top: 8px; }

        /* ===== MODULES ===== */
        .v40-modules { background: #FFF; padding: 96px 0; }
        .v40-modules .v40-inner { background: #FFF; border: 1px solid var(--ds-border-200); border-radius: 16px; padding: 48px; }
        .v40-mod-bands { display: flex; flex-direction: column; margin-top: 28px; }
        .v40-mod-band { display: flex; align-items: flex-start; gap: 32px; padding: 32px 0; border-top: 1px solid var(--ds-border-200); }
        .v40-mod-band:first-child { border-top: none; padding-top: 0; }
        .v40-mod-band:last-child { padding-bottom: 0; }
        .v40-mod-step { flex-shrink: 0; text-align: center; width: 48px; }
        .v40-mod-num-label { margin-top: 2px; }
        .v40-mod-content { flex: 1; min-width: 0; }
        .v40-mod-title { line-height: 1.2; }
        .v40-mod-desc { line-height: 1.55; margin-top: 8px; }
        .v40-mod-note { margin-top: 8px; }
        .v40-mod-note::before { content: '⊞ '; }
        .v40-mod-doc { flex-shrink: 0; width: 260px; background: var(--ds-navy-900); border-radius: 10px; padding: 20px; display: flex; flex-direction: column; gap: 8px; }
        .v40-mod-doc-title { line-height: 1.2; }
        .v40-mod-doc-desc { line-height: 1.45; }

        /* ===== PRICING ===== */
        .v40-pricing { background: #FFF; padding: 96px 0; }
        .v40-pricing-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 32px; }
        .v40-pricing-free { padding: 28px 32px; background: var(--ds-surface-100); border: 1px solid var(--ds-border-200); border-radius: 8px; }
        .v40-pricing-paid { padding: 28px 32px; background: var(--ds-navy-900); border: 1px solid var(--ds-border-800); border-radius: 8px; }
        .v40-pricing-sub { margin-top: 4px; }
        .v40-pricing-divider { height: 1px; margin: 14px 0; }
        .v40-pricing-item { padding: 4px 0; display: flex; align-items: center; gap: 8px; }
        .v40-pricing-item.included { color: var(--ds-navy-900); }
        .v40-pricing-item.excluded { color: var(--ds-text-500); }
        .v40-pricing-item .v40-check { color: var(--ds-green-500); }
        .v40-pricing-item .v40-cross { color: var(--ds-red-500); }
        .v40-pricing-item.white { color: #FFF; }
        .v40-cr-badge { padding: 3px 8px; border-radius: 4px; margin-left: auto; }
        .v40-pricing-link { margin-top: 24px; display: inline-block; background: none; border: none; padding: 0; cursor: pointer; font-family: inherit; }

        /* ===== LEGAL ===== */
        .v40-legal { background: #FFF; padding: 96px 0; }
        .v40-legal-cards { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; margin-top: 28px; }
        .v40-legal-card { padding: 20px 24px; background: var(--ds-surface-100); border: 1px solid var(--ds-border-200); border-radius: 8px; display: flex; flex-direction: column; gap: 8px; }
        .v40-legal-card-body { line-height: 1.45; }
        .v40-trust-banner { padding: 24px 28px; background: var(--ds-navy-900); border: 1px solid var(--ds-border-800); border-radius: 8px; margin-top: 20px; }
        .v40-trust-body { line-height: 1.45; margin-top: 12px; }
        .v40-badges { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 16px; }

        /* ===== BENEFITS SLIDER ===== */
        .v40-benefits { background: var(--ds-navy-900); padding: 96px 0; }
        .v40-benefits-slider { display: flex; align-items: center; gap: 20px; margin-top: 32px; justify-content: center; }
        .v40-benefits-arrow { width: 56px; height: 56px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: none; cursor: pointer; flex-shrink: 0; transition: all 150ms; }
        .v40-benefits-arrow:focus-visible { outline: 2px solid var(--ds-blue-400); outline-offset: 2px; }
        .v40-benefits-arrow.left { background: transparent; border: 1px solid var(--ds-border-700); }
        .v40-benefits-arrow.left:hover { border-color: var(--ds-blue-400); }
        .v40-benefits-arrow.right { background: var(--ds-blue-600); }
        .v40-benefits-arrow.right:hover { background: var(--ds-blue-700); }
        .v40-benefit-slide { flex: 1; max-width: 800px; padding: 32px; background: #FFF; border: 1px solid var(--ds-border-200); border-radius: 16px; }
        .v40-benefit-slide-head { display: flex; justify-content: space-between; align-items: center; }
        .v40-benefit-tag { padding: 4px 12px; background: #F0FDF4; border: 1px solid #BBF7D0; border-radius: 999px; }
        .v40-benefit-title { line-height: 1.12; margin-top: 16px; }
        .v40-benefit-body { line-height: 1.6; margin-top: 12px; }
        .v40-benefit-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-top: 24px; }
        .v40-benefit-stat { padding: 14px 16px; background: var(--ds-surface-100); border: 1px solid var(--ds-border-200); border-radius: 8px; }
        .v40-benefit-stat-label { margin-top: 4px; line-height: 1.4; }
        .v40-benefit-footer { display: flex; justify-content: space-between; align-items: center; margin-top: 20px; }
        .v40-benefit-dots { display: flex; gap: 6px; justify-content: center; margin-top: 20px; }
        .v40-benefit-dots { align-items: center; }
        .v40-benefit-dot { width: 24px; height: 4px; border-radius: 2px; background: var(--ds-border-700); transition: all 200ms; }
        .v40-benefit-dot.active { background: var(--ds-blue-400); width: 32px; }
        .v40-slider-toggle { width: 28px; height: 28px; border-radius: 50%; border: 1px solid var(--ds-border-700); background: transparent; color: var(--ds-text-400); cursor: pointer; display: inline-flex; align-items: center; justify-content: center; margin-left: 10px; transition: all 0.2s ease; flex-shrink: 0; }
        .v40-slider-toggle:hover { border-color: var(--ds-blue-400); color: var(--ds-blue-400); }
        .v40-slider-toggle:focus-visible { outline: 2px solid var(--ds-blue-400); outline-offset: 2px; }

        /* ===== PERSONAS ===== */
        .v40-personas { background: var(--ds-surface-100); padding: 96px 0; }
        .v40-persona-cards { display: flex; flex-direction: column; gap: 16px; margin-top: 28px; }
        .v40-persona-card { padding: 24px 28px; background: #FFF; border: 1px solid var(--ds-border-200); border-radius: 8px; display: flex; flex-direction: column; gap: 12px; }
        .v40-persona-title { line-height: 1.18; }
        .v40-persona-body { line-height: 1.4; }
        .v40-persona-sub { line-height: 1.45; }

        /* ===== FAQ ===== */
        .v40-faq { background: var(--ds-surface-100); padding: 96px 0; }
        .v40-faq-list { display: flex; flex-direction: column; gap: 10px; margin-top: 24px; }
        .v40-faq-item { background: #FFF; border: 1px solid var(--ds-border-300); }
        .v40-faq-trigger { display: flex; justify-content: space-between; align-items: center; gap: 18px; padding: 16px 18px; cursor: pointer; width: 100%; background: none; border: none; text-align: left; }
        .v40-faq-answer { padding: 0 18px 16px; line-height: 1.45; }

        /* ===== ABOUT ===== */
        .v40-about { background: #FFF; padding: 96px 0; }
        .v40-about-body { display: flex; gap: 32px; margin-top: 32px; }
        .v40-about-left { flex: 1; }
        .v40-about-desc { line-height: 1.5; }
        .v40-about-certs { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 20px; }
        .v40-about-right { width: 420px; flex-shrink: 0; display: flex; flex-direction: column; gap: 10px; }
        .v40-about-svc { padding: 16px 18px; background: var(--ds-surface-100); border: 1px solid var(--ds-border-200); border-radius: 8px; display: flex; flex-direction: column; gap: 6px; }
        .v40-about-svc-title { line-height: 1.2; }
        .v40-about-svc-body { line-height: 1.45; }
        .v40-contact-form { padding: 28px; background: var(--ds-surface-100); border: 1px solid var(--ds-border-200); border-radius: 12px; display: flex; flex-direction: column; gap: 16px; }
        .v40-contact-form label { display: block; margin-bottom: 6px; }
        .v40-contact-form select, .v40-contact-form textarea { width: 100%; padding: 10px 14px; border: 1px solid var(--ds-border-300); border-radius: 8px; font-family: 'Roboto Mono', monospace; font-size: 13px; color: var(--ds-navy-900); background: #FFF; resize: vertical; }
        .v40-contact-form select:focus, .v40-contact-form textarea:focus { outline: 2px solid var(--ds-blue-600); outline-offset: 1px; border-color: var(--ds-blue-600); }
        .v40-contact-form textarea { min-height: 80px; }
        .v40-contact-form .v40-btn { width: 100%; justify-content: center; }

        /* ===== CYFUN CTA ===== */
        .v40-cyfun { background: var(--ds-navy-900); padding: 96px 0; text-align: center; }

        /* ===== FINAL CTA + FOOTER ===== */
        .v40-final { background: var(--ds-navy-800); padding: 96px 0; }

        /* ===== SMOOTH SCROLL ===== */
        .v40 { scroll-behavior: smooth; }

        /* ===== PROGRESSIVE REVEAL ===== */
        @keyframes v40-fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .v40-reveal { opacity: 0; transform: translateY(20px); }
        .v40-revealed { animation: v40-fadeInUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) forwards; }
        .v40-revealed.v40-d1 { animation-delay: 120ms; }
        .v40-revealed.v40-d2 { animation-delay: 240ms; }
        .v40-revealed.v40-d3 { animation-delay: 360ms; }
        .v40-revealed.v40-d4 { animation-delay: 480ms; }
        .v40-revealed.v40-d5 { animation-delay: 600ms; }

        /* Legacy section reveal — no longer hides sections, only used for stagger card triggers */
        .v40-section-reveal { }
        .v40-section-visible { }

        /* ===== CARD HOVER INTERACTIONS ===== */
        .v40-mod-card { transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.25s ease, border-color 0.25s ease; }
        .v40-mod-card:hover { transform: translateY(-4px); box-shadow: 0 12px 32px rgba(0,0,0,0.25); border-color: var(--ds-text-600); }
        .v40-pain-card { transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.25s ease, border-color 0.25s ease; }
        .v40-pain-card:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,0.2); border-color: var(--ds-text-600); }
        .v40-legal-card { transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.25s ease, border-color 0.25s ease; }
        .v40-legal-card:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,0.06); border-color: var(--ds-border-300); }
        .v40-persona-card { transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.25s ease, border-color 0.25s ease; }
        .v40-persona-card:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,0.06); border-color: var(--ds-border-300); }
        .v40-timeline-item { transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease; }
        .v40-timeline-item:hover { transform: translateX(4px); box-shadow: 0 4px 16px rgba(0,0,0,0.05); border-color: var(--ds-border-300); }
        .v40-about-svc { transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.25s ease, border-color 0.25s ease; }
        .v40-about-svc:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.06); border-color: var(--ds-border-300); }
        .v40-testimonial { transition: transform 0.25s ease, box-shadow 0.25s ease; }
        .v40-testimonial:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.06); }
        .v40-benefit-stat { transition: transform 0.2s ease, box-shadow 0.2s ease; }
        .v40-benefit-stat:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.06); }

        /* ===== FAQ ACCORDION ANIMATION ===== */
        .v40-faq-item { border-radius: 8px; overflow: hidden; transition: box-shadow 0.2s ease; }
        .v40-faq-item:hover { box-shadow: 0 2px 12px rgba(0,0,0,0.05); }
        .v40-faq-answer-wrap { display: grid; grid-template-rows: 0fr; transition: grid-template-rows 0.35s cubic-bezier(0.22, 1, 0.36, 1); will-change: grid-template-rows; }
        .v40-faq-answer-wrap.open { grid-template-rows: 1fr; }
        .v40-faq-answer-inner { overflow: hidden; }
        .v40-faq-trigger { transition: background 0.15s ease; }
        .v40-faq-trigger:hover { background: rgba(0,0,0,0.02); }

        /* ===== BUTTON ENHANCEMENTS ===== */
        .v40-nav-ghost { transition: all 0.2s ease; }
        .v40-nav-ghost:hover { border-color: #60A5FA; color: #FFF; background: rgba(96,165,250,0.08); }
        .v40-nav-cta { transition: all 0.2s ease; }
        .v40-nav-cta:hover { background: #FF9520; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(255,132,0,0.3); }
        .v40-tab { transition: all 0.2s ease; }
        .v40-tab:not(.active):hover { background: #F1F5F9; border-color: #94A3B8; }
        .v40-pricing-link { transition: color 0.15s ease; }
        .v40-pricing-link:hover { color: var(--ds-blue-700); }

        /* ===== STICKY NAV ===== */
        .v40-hero { position: relative; }
        .v40-nav { transition: background 0.3s ease, box-shadow 0.3s ease, padding 0.3s ease; z-index: 100; }
        .v40-nav.v40-nav-sticky { position: fixed; top: 0; left: 0; right: 0; background: rgba(15,23,42,0.92); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); box-shadow: 0 1px 0 rgba(255,255,255,0.06); padding: 12px 24px; will-change: transform; }
        .v40-nav-spacer { height: 0; transition: height 0.3s ease; }
        .v40-nav-spacer.active { height: 62px; }

        /* ===== ACTIVE NAV LINK ===== */
        .v40-nav-link.v40-nav-active { color: #60A5FA; }

        /* ===== STAGGERED CARD ANIMATIONS ===== */
        @keyframes v40-cardStaggerIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .v40-stagger-card { opacity: 0; }
        .v40-stagger-visible .v40-stagger-card {
          animation: v40-cardStaggerIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .v40-stagger-visible .v40-stagger-card:nth-child(1) { animation-delay: 0ms; }
        .v40-stagger-visible .v40-stagger-card:nth-child(2) { animation-delay: 100ms; }
        .v40-stagger-visible .v40-stagger-card:nth-child(3) { animation-delay: 200ms; }
        .v40-stagger-visible .v40-stagger-card:nth-child(4) { animation-delay: 300ms; }

        /* ===== CUI INPUT FOCUS GLOW ===== */
        .v40-cui-search { transition: border-color 0.2s ease, box-shadow 0.2s ease; }
        .v40-cui-search:focus-within { border-color: var(--ds-blue-600); box-shadow: 0 0 0 3px rgba(37,99,235,0.15), 0 0 16px rgba(37,99,235,0.08); }

        /* ===== DEMO BADGE SHIMMER ===== */
        @keyframes v40-shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        .v40-cui-demo-label-shimmer {
          background: linear-gradient(90deg, var(--ds-border-800) 30%, var(--ds-border-700) 50%, var(--ds-border-800) 70%);
          background-size: 200% 100%;
          animation: v40-shimmer 3s ease-in-out infinite;
        }

        /* ===== SECTION DIVIDER ACCENTS ===== */
        .v40-cui { position: relative; }
        .v40-cui::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(to bottom, var(--ds-navy-900), transparent); pointer-events: none; }
        .v40-cycle { position: relative; }
        .v40-cycle::before { content: ''; position: absolute; top: -1px; left: 0; right: 0; height: 4px; background: linear-gradient(to bottom, rgba(248,250,255,0.5), transparent); pointer-events: none; }
        .v40-obligations { position: relative; }
        .v40-obligations::before { content: ''; position: absolute; top: -1px; left: 0; right: 0; height: 4px; background: linear-gradient(to bottom, rgba(255,255,255,0.4), transparent); pointer-events: none; }
        .v40-benefits { position: relative; }
        .v40-benefits::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px; background: linear-gradient(to bottom, rgba(248,250,255,0.3), transparent); pointer-events: none; }
        .v40-final { position: relative; }
        .v40-final::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px; background: linear-gradient(to bottom, rgba(248,250,255,0.2), transparent); pointer-events: none; }
        .v40-faq { position: relative; }
        .v40-faq::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px; background: linear-gradient(to bottom, rgba(17,24,39,0.15), transparent); pointer-events: none; }

        /* ===== SCROLL PROGRESS BAR ===== */
        .v40-scroll-progress { position: fixed; top: 0; left: 0; height: 2px; background: linear-gradient(90deg, var(--ds-blue-600), var(--ds-blue-400)); z-index: 200; pointer-events: none; transition: width 80ms linear; will-change: width; }

        /* ===== BACK-TO-TOP BUTTON ===== */
        .v40-back-to-top { position: fixed; bottom: 96px; right: 28px; z-index: 90; width: 44px; height: 44px; border-radius: 50%; background: var(--ds-blue-600); color: #FFF; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 16px rgba(37,99,235,0.35); opacity: 0; transform: translateY(16px) scale(0.85); transition: opacity 0.3s ease, transform 0.3s ease, background 0.15s ease; pointer-events: none; }
        .v40-back-to-top.visible { opacity: 1; transform: translateY(0) scale(1); pointer-events: auto; }
        .v40-back-to-top:hover { background: var(--ds-blue-700); transform: translateY(-2px) scale(1.05); }
        .v40-back-to-top:focus-visible { outline: 2px solid var(--ds-blue-400); outline-offset: 3px; }

        /* ===== MOBILE MENU SLIDE ANIMATION ===== */
        .v40-mobile-menu { transform-origin: top center; }
        .v40-mobile-menu-enter { animation: v40-menuSlideDown 0.3s cubic-bezier(0.22, 1, 0.36, 1) forwards; }
        @keyframes v40-menuSlideDown {
          from { opacity: 0; transform: translateY(-12px) scaleY(0.95); }
          to { opacity: 1; transform: translateY(0) scaleY(1); }
        }

        /* ===== FAQ ACTIVE/OPEN HIGHLIGHT ===== */
        .v40-faq-item.v40-faq-open { background: var(--ds-surface-100); border-color: var(--ds-blue-600); box-shadow: 0 2px 12px rgba(37,99,235,0.08); }

        /* ===== HERO METRIC COUNTER ===== */
        .v40-hero-metric-val { font-variant-numeric: tabular-nums; }

        /* ===== CYCLE PROGRESS LINE ===== */
        .v40-cycle-left { position: relative; }
        .v40-cycle-progress-line { position: absolute; left: 36px; top: 42px; bottom: 42px; width: 2px; background: linear-gradient(to bottom, #60A5FA 0%, #60A5FA 36%, #F59E0B 50%, #F59E0B 64%, #10B981 80%, #10B981 100%); border-radius: 1px; pointer-events: none; opacity: 0.5; }

        /* ===== PRICING CARD HIGHLIGHT ===== */
        .v40-pricing-paid { position: relative; transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.3s ease; }
        .v40-pricing-paid:hover { transform: translateY(-4px); box-shadow: 0 16px 48px rgba(37,99,235,0.2), 0 4px 16px rgba(0,0,0,0.3); }
        .v40-pricing-recommended { position: absolute; top: -14px; right: 24px; padding: 6px 16px; background: linear-gradient(135deg, var(--ds-blue-600), var(--ds-blue-700)); border-radius: 999px; box-shadow: 0 2px 8px rgba(37,99,235,0.4); z-index: 1; }
        .v40-pricing-paid-border { border-color: var(--ds-blue-600); border-width: 2px; }

        /* ===== CONTACT FORM VALIDATION ===== */
        .v40-email-input { transition: border-color 0.2s ease, box-shadow 0.2s ease; }
        .v40-email-input.v40-email-error { border-color: var(--ds-red-500) !important; box-shadow: 0 0 0 3px rgba(239,68,68,0.12); }
        .v40-email-input.v40-email-success { border-color: var(--ds-green-600) !important; box-shadow: 0 0 0 3px rgba(16,185,129,0.12); }
        .v40-email-feedback { display: flex; align-items: center; gap: 6px; margin-top: 6px; }
        .v40-email-feedback.error { color: var(--ds-red-500); }
        .v40-email-feedback.success { color: var(--ds-green-600); }
        .v40-select-feedback { display: inline-flex; align-items: center; gap: 6px; margin-top: 6px; opacity: 0; transition: opacity 0.3s ease; }
        .v40-select-feedback.visible { opacity: 1; }

        /* ===== REDUCED MOTION ===== */
        @media (prefers-reduced-motion: reduce) {
          .v40 { scroll-behavior: auto; }
          .v40-section-reveal, .v40-reveal { opacity: 1; transform: none; }
          .v40-section-visible, .v40-revealed { animation: none; opacity: 1; transform: none; }
          .v40-stagger-card { opacity: 1; }
          .v40-stagger-visible .v40-stagger-card { animation: none; opacity: 1; }
          .v40-cui-demo-label-shimmer { animation: none; }
          .v40-cui-skeleton { animation: none; }
          .v40-mod-card, .v40-pain-card, .v40-legal-card, .v40-persona-card,
          .v40-timeline-item, .v40-about-svc, .v40-testimonial, .v40-benefit-stat,
          .v40-faq-item, .v40-faq-answer-wrap, .v40-btn, .v40-btn-primary,
          .v40-nav-ghost, .v40-nav-cta, .v40-tab, .v40-nav, .v40-cui-search,
          .v40-back-to-top, .v40-mobile-menu-enter, .v40-pricing-paid, .v40-slider-toggle { transition: none; animation: none; }
          .v40-back-to-top.visible { opacity: 1; transform: none; }
        }

        /* ===== PRINT ===== */
        @media print {
          .v40-nav.v40-nav-sticky { position: static; background: none; backdrop-filter: none; box-shadow: none; }
          .v40-scroll-progress, .v40-back-to-top, .v40-mobile-cta, .v40-menu-btn { display: none !important; }
          .v40-hero { background: #FFF !important; }
          .v40-hero-title, .v40-logo-text { color: #0F172A !important; }
          .v40-hero-sub, .v40-hero-proof { color: #334155 !important; }
          .v40-hero-right { background: #F8FAFC !important; border-color: #E2E8F0 !important; }
          .v40-hero-metric-val { color: #0F172A !important; }
          .v40-hero-metric-label, .v40-hero-tool { color: #475569 !important; }
          .v40-disclaimer { background: #F8FAFC !important; }
          .v40-disclaimer-text { color: #334155 !important; }
          .v40-cycle-left, .v40-cui-result, .v40-mod-card, .v40-pricing-paid, .v40-trust-banner, .v40-pain-card { background: #F8FAFC !important; border-color: #E2E8F0 !important; }
          .v40-cycle-level-title, .v40-cui-company-name, .v40-mod-title, .v40-pricing-title { color: #0F172A !important; }
          .v40-cycle-level-desc, .v40-cui-card-body, .v40-mod-desc, .v40-mod-output-text, .v40-mod-note, .v40-pain-card-body { color: #475569 !important; }
          .v40-pain-card-title { color: #0F172A !important; }
          .v40-benefits { background: #FFF !important; }
          .v40-final, .v40-cyfun { background: #F8FAFC !important; }
          .v40 { color: #0F172A; }
          .v40-section-reveal { opacity: 1 !important; transform: none !important; }
          .v40-stagger-card { opacity: 1 !important; }
          .v40-btn-pulse { animation: none !important; }
          .v40-cui-demo-label-shimmer { animation: none !important; }
          * { transition: none !important; animation: none !important; }
        }

        /* ===== MOBILE ===== */
        .v40-menu-btn { display: none; border: 1px solid var(--ds-border-700); background: transparent; color: var(--ds-border-200); border-radius: 10px; width: 44px; height: 44px; align-items: center; justify-content: center; cursor: pointer; }
        .v40-mobile-menu { display: none; position: fixed; inset: 0; z-index: 90; background: var(--ds-navy-900); padding: 24px; flex-direction: column; justify-content: center; align-items: center; gap: 0; }
        .v40-mobile-menu .v40-nav-link { font-size: 20px; padding: 18px 0; width: 100%; text-align: center; border-bottom: 1px solid #1E293B; color: #E2E8F0; }
        .v40-mobile-menu .v40-nav-link:last-of-type { border-bottom: none; }
        .v40-mobile-menu .v40-btn { margin-top: 24px; width: 100%; justify-content: center; font-size: 16px; padding: 18px 28px; }
        .v40-body-locked { overflow: hidden !important; }
        .v40-mobile-cta { display: none; position: sticky; bottom: 0; z-index: 80; background: rgba(11,18,32,0.94); backdrop-filter: blur(10px); border-top: 1px solid var(--ds-border-700); padding: 10px 12px; transform: translateY(100%); opacity: 0; transition: transform 0.3s ease, opacity 0.3s ease; }
        .v40-mobile-cta.visible { transform: translateY(0); opacity: 1; }
        .v40-mobile-cta button { width: 100%; justify-content: center; }

        @media (max-width: 1080px) {
          .v40-hero-body, .v40-cycle-body, .v40-cui-grid, .v40-cui-skeleton-grid, .v40-about-body { flex-direction: column; }
          .v40-hero-right, .v40-cycle-right, .v40-about-right { width: 100%; }
          .v40-pricing-grid, .v40-legal-cards, .v40-pain-cards { grid-template-columns: 1fr; }
          .v40-benefit-stats { grid-template-columns: 1fr; }
          .v40-benefit-title { font-size: 22px; }
          .v40-benefits-slider { flex-direction: column; }
          .v40-benefits-arrow { display: none; }
          .v40-cui-company, .v40-cui-skeleton-company { flex-direction: column; align-items: flex-start; }
        }
        @media (max-width: 768px) {
          .v40-pricing-grid { grid-template-columns: 1fr; }
          .v40-about-body { flex-direction: column; }
          .v40-about-right { width: 100%; }
          .v40-benefit-slide { padding: 24px; }
          .v40-benefit-title { font-size: 20px; }
          .v40-mod-band { flex-direction: column; gap: 16px; }
          .v40-mod-doc { width: 100%; }
          .v40-modules .v40-inner { padding: 24px; }
          .v40-legal-cards { grid-template-columns: 1fr; }
          .v40-pain-cards { grid-template-columns: 1fr; }
          .v40-cui-grid, .v40-cui-skeleton-grid { flex-direction: column; gap: 24px; }
          .v40-cycle-body { flex-direction: column; }
          .v40-cycle-right { width: 100%; }
        }
        @media (max-width: 920px) {
          .v40-inner, .v40-hero > * { padding-left: 16px; padding-right: 16px; }
          .v40-nav-links, .v40-nav-btns { display: none; }
          .v40-menu-btn { display: inline-flex; }
          .v40-mobile-menu { display: flex; }
          .v40-mobile-cta { display: flex; }
          .v40-hero-title { font-size: 36px; }
          .v40-benefit-stats { grid-template-columns: 1fr; }
          .v40-disclaimer { flex-direction: column; align-items: flex-start; gap: 12px; }
          .v40-hero-metric-val { font-size: 48px; }
          .v40-hero-metrics { gap: 24px; }
          .v40-hero-right { padding: 16px; }
          .v40-faq-trigger { padding: 20px 18px; }
          .v40-faq-trigger-text { font-size: 16px; }
          .v40-back-to-top { bottom: 120px; right: 16px; }
          .v40-tabs { flex-wrap: wrap; gap: 6px; }
          .v40-tab { padding: 10px 12px; font-size: 11px; white-space: normal; text-align: center; }
          .v40-pricing-recommended { right: 16px; top: -12px; font-size: 10px; padding: 5px 12px; }
        }
      `}</style>

      {/* ===== SCROLL PROGRESS BAR ===== */}
      <div
        className="v40-scroll-progress"
        style={{ width: `${scrollProgress * 100}%` }}
        role="presentation"
      />

      <a href="#main-content" className="v40-skip">
        Sari la continut
      </a>

      <main id="main-content">
        {/* ====== SECTION 1: HERO ====== */}
        <section className="v40-hero bg-navy-900">
          <nav className={`v40-nav${navScrolled ? " v40-nav-sticky" : ""}`}>
            <button
              type="button"
              className="v40-logo"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              <span className="v40-logo-mark" />
              <span className="v40-logo-text">NIS2PASS</span>
            </button>
            <div
              className="v40-nav-links"
              role="navigation"
              aria-label="Navigare sectiuni"
            >
              {navSectionIds.map((s) => (
                <button
                  type="button"
                  key={s.label}
                  className={`v40-nav-link${activeNavSection === s.id ? " v40-nav-active" : ""}`}
                  onClick={() =>
                    document
                      .getElementById(s.id)
                      ?.scrollIntoView({ behavior: "smooth" })
                  }
                >
                  {s.label}
                </button>
              ))}
              <button
                type="button"
                className="v40-nav-link"
                onClick={() => navigate("/contact")}
              >
                Contact
              </button>
            </div>
            <div className="v40-nav-btns">
              {!loading &&
                (isLoggedIn ? (
                  <>
                    <button
                      type="button"
                      className="v40-nav-cta"
                      onClick={() => navigate("/companiile-mele")}
                    >
                      Dashboard
                    </button>
                    <button
                      type="button"
                      className="v40-nav-ghost"
                      onClick={() => navigate("/logout")}
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="v40-nav-ghost"
                      onClick={() => navigate("/auth?mode=signin")}
                    >
                      Intra in cont
                    </button>
                    <button
                      type="button"
                      className="v40-nav-cta"
                      onClick={() => navigate("/auth")}
                    >
                      Creeaza cont gratuit
                    </button>
                  </>
                ))}
            </div>
            <button
              type="button"
              className="v40-menu-btn"
              ref={menuButtonRef}
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? (
                <X size={18} aria-hidden="true" />
              ) : (
                <Menu size={18} aria-hidden="true" />
              )}
            </button>
          </nav>
          <div className={`v40-nav-spacer${navScrolled ? " active" : ""}`} />

          {mobileMenuOpen && (
            <div
              className="v40-mobile-menu v40-mobile-menu-enter"
              role="navigation"
              aria-label="Meniu mobil"
              ref={mobileMenuRef}
            >
              <button
                type="button"
                className="v40-nav-link"
                onClick={() => {
                  setMobileMenuOpen(false);
                  setTimeout(
                    () =>
                      document
                        .getElementById("v40-sec-cycle")
                        ?.scrollIntoView({ behavior: "smooth" }),
                    50
                  );
                }}
              >
                Cum functioneaza
              </button>
              <button
                type="button"
                className="v40-nav-link"
                onClick={() => {
                  setMobileMenuOpen(false);
                  setTimeout(
                    () =>
                      document
                        .getElementById("v40-sec-modules")
                        ?.scrollIntoView({ behavior: "smooth" }),
                    50
                  );
                }}
              >
                Module
              </button>
              <button
                type="button"
                className="v40-nav-link"
                onClick={() => {
                  setMobileMenuOpen(false);
                  setTimeout(
                    () =>
                      document
                        .getElementById("v40-sec-pricing")
                        ?.scrollIntoView({ behavior: "smooth" }),
                    50
                  );
                }}
              >
                Preturi
              </button>
              <button
                type="button"
                className="v40-nav-link"
                onClick={() => {
                  setMobileMenuOpen(false);
                  setTimeout(
                    () =>
                      document
                        .getElementById("v40-sec-legal")
                        ?.scrollIntoView({ behavior: "smooth" }),
                    50
                  );
                }}
              >
                Cadru legal
              </button>
              <button
                type="button"
                className="v40-nav-link"
                onClick={() => {
                  setMobileMenuOpen(false);
                  setTimeout(
                    () =>
                      document
                        .getElementById("v40-sec-faq")
                        ?.scrollIntoView({ behavior: "smooth" }),
                    50
                  );
                }}
              >
                FAQ
              </button>
              <button
                type="button"
                className="v40-nav-link"
                onClick={() => {
                  setMobileMenuOpen(false);
                  setTimeout(
                    () =>
                      document
                        .getElementById("v40-sec-about")
                        ?.scrollIntoView({ behavior: "smooth" }),
                    50
                  );
                }}
              >
                Despre noi
              </button>
              <button
                type="button"
                className="v40-nav-link"
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate("/contact");
                }}
              >
                Contact
              </button>
              {isLoggedIn ? (
                <>
                  <button
                    type="button"
                    className="v40-btn v40-btn-primary font-medium text-sm font-mono bg-ds-orange-500 text-[#111]"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate("/companiile-mele");
                    }}
                  >
                    Dashboard
                  </button>
                  <button
                    type="button"
                    className="v40-btn v40-btn-ghost"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate("/logout");
                    }}
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="v40-btn v40-btn-ghost"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate("/auth?mode=signin");
                    }}
                  >
                    Intra in cont
                  </button>
                  <button
                    type="button"
                    className="v40-btn v40-btn-primary font-medium text-sm font-mono bg-ds-orange-500 text-[#111]"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      startEvaluation();
                    }}
                  >
                    Incepe evaluarea <ArrowRight size={16} aria-hidden="true" />
                  </button>
                </>
              )}
            </div>
          )}

          <div className="v40-hero-body">
            <div className="v40-hero-left">
              <h1 className="v40-hero-title font-heading text-ds-h1 font-extrabold text-white tracking-[-2px] leading-[1.05]">
                Rezolva conformitatea NIS2 in Romania.
              </h1>
              <p className="v40-hero-sub text-[15px] leading-[1.45] text-ds-blue-200">
                Ghidam organizatiile din Romania prin toate cerintele Directivei
                NIS2 transpusa prin OUG 155/2024 — de la evaluarea
                aplicabilitatii si analiza de risc, pana la autoevaluarea CyFun
                si generarea pachetului complet de documente pentru DNSC.
              </p>
              <div>
                <button
                  type="button"
                  className="v40-btn v40-btn-primary v40-hero-cta"
                  onClick={startEvaluation}
                >
                  {isLoggedIn
                    ? "Mergi la Companiile mele"
                    : "Incepe conformarea"}{" "}
                  <ArrowRight size={16} aria-hidden="true" />
                </button>
              </div>
              <p className="v40-hero-proof text-xs text-[#A8B8CB] italic">
                Verificare aplicabilitate NIS2 dupa CUI · 425+ evaluari generate
              </p>
            </div>
            <div className="v40-hero-right">
              <div className="v40-hero-metrics" ref={metricsRef}>
                <div>
                  <p className="v40-hero-metric-val font-heading text-[64px] font-extrabold text-white">
                    {metric1}m
                  </p>
                  <p className="v40-hero-metric-label text-ds-xxs text-ds-text-400">
                    Flux mediu evaluare initiala
                  </p>
                </div>
                <div>
                  <p className="v40-hero-metric-val font-heading text-[64px] font-extrabold text-white">
                    {metric2}+
                  </p>
                  <p className="v40-hero-metric-label text-ds-xxs text-ds-text-400">
                    Rapoarte DNSC generate
                  </p>
                </div>
              </div>
              <div className="v40-hero-tools">
                <span className="v40-hero-tool text-xs text-ds-text-300">
                  <span className="v40-hero-tool-check text-ds-green-500">
                    ✓
                  </span>{" "}
                  Formular de Notificare
                </span>
                <span className="v40-hero-tool text-xs text-ds-text-300">
                  <span className="v40-hero-tool-check text-ds-green-500">
                    ✓
                  </span>{" "}
                  Analiza Efect Perturbator
                </span>
                <span className="v40-hero-tool text-xs text-ds-text-300">
                  <span className="v40-hero-tool-check text-ds-green-500">
                    ✓
                  </span>{" "}
                  Analiza Nivel de Risc
                </span>
                <span className="v40-hero-tool text-xs text-ds-text-300">
                  <span className="v40-hero-tool-check text-ds-green-500">
                    ✓
                  </span>{" "}
                  Autoevaluare Maturitate
                </span>
              </div>
            </div>
          </div>

          <div className="v40-disclaimer">
            <div className="v40-disclaimer-badge">
              <span className="v40-disclaimer-dot bg-ds-amber-500" />
              <span className="v40-disclaimer-badge-text text-ds-xxs font-medium text-ds-surface-100 tracking-[1.2px]">
                NOTA IMPORTANTA
              </span>
            </div>
            <p className="v40-disclaimer-text text-xs text-ds-border-200">
              Instrument auxiliar de conformare NIS2. Rezultatele sunt
              preliminare — decizia finala de clasificare ca entitate esentiala
              sau importanta apartine exclusiv DNSC conform OUG 155/2024.
            </p>
          </div>
        </section>

        {/* ====== SECTION 2: CUI VERIFICATION ====== */}
        <CuiVerificationSection
          sectionRef={setSectionRef(0)}
          isLoggedIn={isLoggedIn}
          onCreateAccountWithCui={handleCreateAccountWithCui}
          isCreatingSession={isCreatingSession}
          userSessions={userSessions}
          archivedUserSessions={archivedSessions}
        />

        {/* ====== SECTION 3: CYCLE + TESTIMONIAL ====== */}
        <section
          className="v40-cycle v40-section-reveal"
          ref={setSectionRef(1)}
          id="v40-sec-cycle"
        >
          <div className="v40-inner">
            <p className="v40-eyebrow text-xs tracking-[1.4px] text-ds-blue-600 font-medium font-mono v40-reveal">
              PLATFORMA NIS2PASS
            </p>
            <h2
              className="v40-h2 font-heading text-ds-h2 font-bold tracking-[-1.3px] leading-[1.08] text-navy-900 v40-reveal v40-d1"
              style={{ marginTop: 16, maxWidth: 900 }}
            >
              Acopera intreg ciclul de conformitate NIS2 — de la notificare la
              audit
            </h2>
            <p
              className="v40-body text-sm leading-[1.45] text-slate-700 v40-reveal v40-d2"
              style={{ marginTop: 16, maxWidth: 800, lineHeight: 1.6 }}
            >
              De la completarea formularului de notificare NIS2 pana la auditul
              final — 7 niveluri integrate intr-o singura platforma. Evaluare
              efect perturbator, clasificare risc ENIRE@RO si autoevaluare
              maturitate CyFun, toate ghidate pas cu pas.
            </p>
            <div className="v40-cycle-body v40-reveal v40-d3">
              <div className="v40-cycle-left">
                <div className="v40-cycle-progress-line" aria-hidden="true" />
                {cycleLevels.map((lvl, i) => (
                  <div key={lvl.n} className="v40-cycle-level">
                    <span
                      className="v40-cycle-dot"
                      style={{
                        background: lvl.color,
                        borderStyle: lvl.conditional ? "dashed" : undefined,
                      }}
                    />
                    <div>
                      <p
                        className="v40-cycle-level-label text-ds-xxxs tracking-[1.4px]"
                        style={{ color: lvl.color }}
                      >
                        {lvl.n}
                        {lvl.conditional && (
                          <span
                            style={{ marginLeft: 8, fontSize: 9, opacity: 0.7 }}
                          >
                            (conditionat)
                          </span>
                        )}
                      </p>
                      <p className="v40-cycle-level-title font-heading text-[15px] font-semibold text-white">
                        {lvl.title}
                      </p>
                      <p className="v40-cycle-level-desc text-xs text-ds-text-300">
                        {lvl.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="v40-cycle-right">
                <p
                  className="v40-eyebrow text-xs tracking-[1.4px] text-ds-blue-600 font-medium font-mono"
                  style={{ fontWeight: 700 }}
                >
                  CE SPUN CISO
                </p>
                <h3
                  className="v40-sora font-heading"
                  style={{
                    fontSize: 24,
                    fontWeight: 700,
                    color: "#0F172A",
                    lineHeight: 1.2,
                  }}
                >
                  Testimonial din implementare reala
                </h3>
                <div className="v40-testimonial">
                  <span className="v40-testimonial-stars text-sm font-bold text-ds-blue-600 tracking-[1px]">
                    ★★★★★
                  </span>
                  <p className="v40-testimonial-quote text-sm text-ds-border-700">
                    «Inainte de NIS2PASS ne lua 3-4 saptamani sa pregatim
                    analiza de risc si evaluarea de maturitate. Acum finalizam
                    acelasi flux in 4 zile, cu trasabilitate clara pentru audit
                    si raportare catre DNSC.»
                  </p>
                  <div>
                    <p className="v40-testimonial-author font-heading text-base font-bold text-navy-900">
                      Andrei M., CISO
                    </p>
                    <p className="v40-testimonial-role text-xs text-ds-text-500">
                      Operator servicii esentiale · Energie
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ====== SECTION 4: OBLIGATIONS TIMELINE ====== */}
        <section
          className="v40-obligations v40-section-reveal"
          ref={setSectionRef(2)}
        >
          <div className="v40-inner">
            <p className="v40-eyebrow text-xs tracking-[1.4px] text-ds-blue-600 font-medium font-mono v40-reveal">
              OBLIGATII NIS2 ROMANIA · OUG 155/2024
            </p>
            <h2
              className="v40-h2 font-heading text-ds-h2 font-bold tracking-[-1.3px] leading-[1.08] text-navy-900 v40-reveal v40-d1"
              style={{ marginTop: 16 }}
            >
              Obligatiile NIS2 din Romania: termene, instrumente si pasi de
              conformitate
            </h2>
            <p
              className="v40-body text-sm leading-[1.45] text-slate-700 v40-reveal v40-d2"
              style={{ marginTop: 16, maxWidth: 1000 }}
            >
              OUG 155/2024 impune un proces continuu de conformitate NIS2
              structurat in patru categorii: inregistrarea pe platforma NIS2@RO,
              obligatiile legale cu termene stricte, implementarea masurilor de
              securitate cibernetica, si monitorizarea continua cu raportare
              catre DNSC.
            </p>
            <div
              className="v40-tabs v40-reveal v40-d3"
              role="tablist"
              aria-label="Categorii obligatii NIS2"
            >
              {[
                "CAT 1 · Inregistrare",
                "CAT 2 · Obligatii",
                "CAT 3 · Implementare",
                "CAT 4 · Monitorizare",
              ].map((label, i) => (
                <button
                  key={label}
                  type="button"
                  className={`v40-tab text-xs font-medium${obligTab === i ? " active" : ""}`}
                  role="tab"
                  aria-selected={obligTab === i}
                  onClick={() => setObligTab(i)}
                >
                  {label}
                </button>
              ))}
            </div>

            {obligTab === 0 && (
              <div className="v40-timeline">
                <div className="v40-timeline-item">
                  <div>
                    <span className="v40-timeline-step text-ds-xxs text-ds-blue-600 tracking-[1px] font-medium">
                      PAS 01
                    </span>
                    <p className="v40-timeline-title font-heading text-base font-bold text-navy-900">
                      Evaluare aplicabilitate NIS2 — cod CAEN si dimensiune
                    </p>
                    <p className="v40-timeline-desc text-xs text-ds-text-600">
                      Verificare prin CUI, clasificare entitate conform OUG
                      155/2024
                    </p>
                  </div>
                  <span
                    className="v40-timeline-tag text-ds-xxs"
                    style={{ background: "#F0F4FF", color: "#1D4ED8" }}
                  >
                    Pre-inregistrare
                  </span>
                </div>
                <div className="v40-timeline-item">
                  <div>
                    <span className="v40-timeline-step text-ds-xxs text-ds-blue-600 tracking-[1px] font-medium">
                      PAS 02
                    </span>
                    <p className="v40-timeline-title font-heading text-base font-bold text-navy-900">
                      Analiza efect perturbator conform Art. 9 OUG 155/2024
                    </p>
                    <p className="v40-timeline-desc text-xs text-ds-text-600">
                      Evaluare impact conform art. 9 si calculul scorului de
                      perturbari
                    </p>
                  </div>
                  <span
                    className="v40-timeline-tag text-ds-xxs"
                    style={{ background: "#FEF3C7", color: "#92400E" }}
                  >
                    La notificare
                  </span>
                </div>
                <div className="v40-timeline-item">
                  <div>
                    <span className="v40-timeline-step text-ds-xxs text-ds-blue-600 tracking-[1px] font-medium">
                      PAS 03/04
                    </span>
                    <p className="v40-timeline-title font-heading text-base font-bold text-navy-900">
                      Completare si transmitere Formular Notificare NIS2@RO
                    </p>
                    <p className="v40-timeline-desc text-xs text-ds-text-600">
                      Generare formular notificare NIS2@RO si DNSC
                    </p>
                  </div>
                </div>
                <div className="v40-timeline-item">
                  <div>
                    <span className="v40-timeline-step text-ds-xxs text-ds-blue-600 tracking-[1px] font-medium">
                      PAS 05
                    </span>
                    <p className="v40-timeline-title font-heading text-base font-bold text-navy-900">
                      Decizia DNSC de Inscriere in Registrul Entitatilor NIS2
                    </p>
                    <p className="v40-timeline-desc text-xs text-ds-text-600">
                      T0 = data comunicarii deciziei DNSC — termenele legale
                    </p>
                  </div>
                </div>
              </div>
            )}

            {obligTab === 1 && (
              <div className="v40-timeline">
                <div className="v40-timeline-item">
                  <div>
                    <span className="v40-timeline-step text-ds-xxs text-ds-blue-600 tracking-[1px] font-medium">
                      OBLIG-01/02
                    </span>
                    <p className="v40-timeline-title font-heading text-base font-bold text-navy-900">
                      Desemnare responsabil securitate cibernetica + Contact
                      permanent DNSC
                    </p>
                    <p className="v40-timeline-desc text-xs text-ds-text-600">
                      Art. 14 alin. (3–4). EE: autoritate manageriala,
                      independent IT, curs acreditat 12 luni
                    </p>
                  </div>
                  <span
                    className="v40-timeline-tag text-ds-xxs"
                    style={{ background: "#FEF3C7", color: "#92400E" }}
                  >
                    T₀ + 30 zile
                  </span>
                </div>
                <div className="v40-timeline-item">
                  <div>
                    <span className="v40-timeline-step text-ds-xxs text-ds-blue-600 tracking-[1px] font-medium">
                      OBLIG-05
                    </span>
                    <p className="v40-timeline-title font-heading text-base font-bold text-navy-900">
                      Evaluarea nivelului de risc (ENIRE@RO)
                    </p>
                    <p className="v40-timeline-desc text-xs text-ds-text-600">
                      Transmitere catre DNSC · Art. 18(6), Art. 10(2) ·
                      Rezultat: De Baza / Important / Esential
                    </p>
                  </div>
                  <span
                    className="v40-timeline-tag text-ds-xxs"
                    style={{ background: "#FEF3C7", color: "#92400E" }}
                  >
                    T₀ + 60 zile
                  </span>
                </div>
                <div className="v40-timeline-item">
                  <div>
                    <span className="v40-timeline-step text-ds-xxs text-ds-blue-600 tracking-[1px] font-medium">
                      OBLIG-07/08
                    </span>
                    <p className="v40-timeline-title font-heading text-base font-bold text-navy-900">
                      Autoevaluare maturitate (EVAL_MMS) + Transmitere catre
                      DNSC
                    </p>
                    <p className="v40-timeline-desc text-xs text-ds-text-600">
                      Chestionar CyFun® pe doua dimensiuni: documentatie si
                      implementare · Art. 12(4), Art. 18(7)
                    </p>
                  </div>
                  <span
                    className="v40-timeline-tag text-ds-xxs"
                    style={{ background: "#DBEAFE", color: "#1E40AF" }}
                  >
                    60 zile de la evaluarea de risc · Apoi ANUAL
                  </span>
                </div>
                <div
                  className="v40-timeline-note"
                  style={{
                    background: "#FFFBEB",
                    border: "1px solid #FDE68A",
                    borderRadius: 8,
                    padding: "12px 16px",
                    marginTop: 8,
                  }}
                >
                  <p
                    style={{ fontSize: 12, color: "#92400E", lineHeight: 1.5 }}
                  >
                    <strong>Nota:</strong> Conform art. 37 alin. (8) lit. b) din
                    OUG 155/2024, termenul de 60 de zile pentru realizarea
                    primei autoevaluari incepe sa curga de la data publicarii in
                    Monitorul Oficial a ordinului DNSC care stabileste cerintele
                    CyFun aplicabile (Ordinul DNSC 4 — in prezent in consultare
                    publica).
                  </p>
                </div>
                <div className="v40-timeline-item">
                  <div>
                    <span className="v40-timeline-step text-ds-xxs text-ds-blue-600 tracking-[1px] font-medium">
                      OBLIG-09
                    </span>
                    <p className="v40-timeline-title font-heading text-base font-bold text-navy-900">
                      Plan de remediere + declaratie apetit de risc
                    </p>
                    <p className="v40-timeline-desc text-xs text-ds-text-600">
                      Gap-uri, prioritati P1–P4, responsabili, buget, termene ·
                      Art. 12 alin. (5)
                    </p>
                  </div>
                  <span
                    className="v40-timeline-tag text-ds-xxs"
                    style={{ background: "#FEF3C7", color: "#92400E" }}
                  >
                    30 zile de la autoevaluare
                  </span>
                </div>
              </div>
            )}

            {obligTab === 2 && (
              <div className="v40-timeline">
                {[
                  {
                    code: "IMP-GOV-01-03",
                    title:
                      "Guvernanta: aprobare conducere, formare, roluri si politici",
                    ref: "Art. 14(1–2), Art. 13(a)(i)",
                  },
                  {
                    code: "IMP-SEC-01",
                    title:
                      "Analiza de risc de securitate cibernetica si revizuire periodica",
                    ref: "Art. 13 lit. (a)",
                  },
                  {
                    code: "IMP-SEC-02",
                    title:
                      "Inventarierea activelor hardware, software, retea, cloud, date",
                    ref: "Art. 13 lit. (f)",
                  },
                  {
                    code: "IMP-SEC-03",
                    title:
                      "Identitati, control acces si autentificare multifactor (MFA)",
                    ref: "Art. 13 lit. (f)(j)",
                  },
                  {
                    code: "IMP-SEC-04",
                    title: "Criptografie si protectia datelor",
                    ref: "Art. 13 lit. (c)",
                  },
                  {
                    code: "IMP-SEC-05",
                    title: "Backup, copii de rezerva si testarea restaurarii",
                    ref: "Art. 13 lit. (h)",
                  },
                  {
                    code: "IMP-SEC-06",
                    title:
                      "Securitate retea: firewall, segmentare, protectie perimetru",
                    ref: "Art. 13 lit. (j)",
                  },
                  {
                    code: "IMP-SEC-07",
                    title: "Detectare, monitorizare continua si jurnalizare",
                    ref: "Art. 13 lit. (b)",
                  },
                  {
                    code: "IMP-SEC-08",
                    title: "Gestionarea incidentelor si raportare PNRISC",
                    ref: "Art. 13 lit. (g), Art. 15",
                  },
                  {
                    code: "IMP-SEC-09",
                    title:
                      "Continuitate, redresare in caz de dezastru, managementul crizelor",
                    ref: "Art. 13 lit. (h)",
                  },
                  {
                    code: "IMP-SEC-10",
                    title: "Securitatea lantului de aprovizionare",
                    ref: "Art. 13 lit. (d) · doar Important + Esential",
                  },
                  {
                    code: "IMP-SEC-11",
                    title:
                      "Securitate ciclul de viata SI si gestionarea vulnerabilitatilor",
                    ref: "Art. 13 lit. (e)",
                  },
                  {
                    code: "IMP-AUD-01",
                    title:
                      "Cadru evaluare eficacitate: KPI/KRI, colectare, raportare",
                    ref: "Art. 13 lit. (b)",
                  },
                ].map((item) => (
                  <div key={item.code} className="v40-timeline-item">
                    <div>
                      <span className="v40-timeline-step text-ds-xxs text-ds-blue-600 tracking-[1px] font-medium">
                        {item.code}
                      </span>
                      <p className="v40-timeline-title font-heading text-base font-bold text-navy-900">
                        {item.title}
                      </p>
                    </div>
                    <span
                      className="v40-timeline-tag text-ds-xxs"
                      style={{ background: "#F0FDF4", color: "#166534" }}
                    >
                      {item.ref}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {obligTab === 3 && (
              <div className="v40-timeline">
                {[
                  {
                    freq: "ANUAL",
                    title: "Autoevaluare maturitate",
                    ref: "MON-01 · Art. 12(4)",
                  },
                  {
                    freq: "LA INCIDENT",
                    title: "Raportare PNRISC",
                    ref: "MON-02 · Art. 15 · ≤24h alerta · ≤72h raport · ≤1 luna final",
                  },
                  {
                    freq: "LA MODIFICARE",
                    title: "Actualizare date DNSC",
                    ref: "MON-03 · Art. 18(8) · 2 sapt date ID / 3 luni date tehnice",
                  },
                  {
                    freq: "LA SOLICITARE",
                    title: "Cooperare DNSC audituri",
                    ref: "MON-04 · Art. 46–51 · Post-control: 15 zile plan + 5 zile acte",
                  },
                  {
                    freq: "TRIMESTRIAL",
                    title: "Scanari vulnerabilitati + KPI/KRI",
                    ref: "MON-05, MON-06 · Art. 13(b)(e)",
                  },
                  {
                    freq: "IMEDIAT",
                    title: "Masuri corective fara intarziere",
                    ref: "MON-12 · Art. 12(3) · La constatarea neconformitatii",
                  },
                ].map((item) => (
                  <div key={item.freq} className="v40-timeline-item">
                    <div>
                      <span className="v40-timeline-step text-ds-xxs text-ds-blue-600 tracking-[1px] font-medium">
                        {item.freq}
                      </span>
                      <p className="v40-timeline-title font-heading text-base font-bold text-navy-900">
                        {item.title}
                      </p>
                    </div>
                    <span
                      className="v40-timeline-tag text-ds-xxs"
                      style={{
                        background: "#F0F4FF",
                        color: "#1D4ED8",
                        fontSize: 11,
                      }}
                    >
                      {item.ref}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div
              className="v40-pain-cards v40-reveal v40-d4"
              ref={setStaggerRef(1)}
            >
              <div className="v40-pain-card v40-stagger-card">
                <p className="v40-pain-card-title font-heading text-base font-semibold text-white">
                  Instrumente DNSC complexe: ENIRE@RO, CyFun, EVAL_MMS
                </p>
                <p className="v40-pain-card-body text-xs text-ds-text-300">
                  Fisiere Excel cu sute de campuri, corelare manuala coduri CAEN
                  cu anexele OUG 155/2024. Fara automatizare, completarea
                  dureaza saptamani.
                </p>
              </div>
              <div className="v40-pain-card v40-stagger-card">
                <p className="v40-pain-card-title font-heading text-base font-semibold text-white">
                  Termene stricte OUG 155/2024 cascadate de la T0
                </p>
                <p className="v40-pain-card-body text-xs text-ds-text-300">
                  30 zile responsabil securitate. 60 zile ENIRE@RO. +60 zile
                  EVAL_MMS. Sanctiuni DNSC pana la 500.000 lei pentru depasire
                  termene.
                </p>
              </div>
              <div className="v40-pain-card v40-stagger-card">
                <p className="v40-pain-card-title font-heading text-base font-semibold text-white">
                  3 categorii, 44 pasi legali interdependenti conform OUG
                  155/2024
                </p>
                <p className="v40-pain-card-body text-xs text-ds-text-300">
                  Fiecare pas depinde de rezultatele anterioare. Nivelul de
                  securitate din ENIRE@RO determina instrumentele EVAL_MMS si
                  masurile obligatorii.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ====== MID-PAGE CTA ====== */}
        <section className="v40-final v40-section-reveal">
          <div className="v40-inner">
            <p
              className="v40-eyebrow text-xs tracking-[1.4px] text-ds-blue-600 font-medium font-mono v40-reveal"
              style={{ color: "#60A5FA" }}
            >
              {isLoggedIn ? "CONTINUA EVALUAREA" : "INCEPE ACUM"}
            </p>
            <h2
              className="v40-sora font-heading v40-reveal v40-d1"
              style={{
                fontSize: "clamp(36px, 4.5vw, 48px)",
                fontWeight: 800,
                color: "#FFF",
                letterSpacing: -1.8,
                lineHeight: 1.06,
                marginTop: 16,
              }}
            >
              {isLoggedIn
                ? "Continua conformitatea NIS2 pentru companiile tale"
                : "Pregatit sa parcurgi conformitatea NIS2?"}
            </h2>
            <p
              className="v40-reveal v40-d2"
              style={{
                fontSize: 14,
                color: "#C7D2FE",
                lineHeight: 1.45,
                maxWidth: 800,
                marginTop: 16,
              }}
            >
              {isLoggedIn
                ? "Reia evaluarea unde ai ramas sau adauga o companie noua. Identificare, evaluare risc ENIRE@RO si autoevaluare maturitate CyFun — toate intr-un singur loc."
                : "Creeaza un cont gratuit si parcurge intreg fluxul de evaluare NIS2 pentru compania ta. 100 de credite incluse — suficiente pentru identificare, evaluare risc ENIRE@RO si autoevaluare maturitate CyFun."}
            </p>
            <div
              className="v40-reveal v40-d3"
              style={{
                display: "flex",
                gap: 12,
                marginTop: 24,
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                className="v40-btn v40-btn-primary v40-hero-cta"
                onClick={startEvaluation}
              >
                {isLoggedIn
                  ? "Mergi la Companiile mele"
                  : "Incepe conformarea — 100 credite gratuite"}{" "}
                <ArrowRight size={16} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="v40-btn v40-btn-ghost"
                onClick={requestDemo}
              >
                Solicita demo
              </button>
            </div>
          </div>
        </section>

        {/* ====== SECTION 5: 4 MODULES ====== */}
        <section
          className="v40-modules v40-section-reveal"
          ref={setSectionRef(3)}
          id="v40-sec-modules"
        >
          <div className="v40-inner">
            <p className="v40-eyebrow text-xs tracking-[1.4px] text-ds-blue-600 font-medium font-mono v40-reveal">
              CUM FUNCTIONEAZA NIS2PASS
            </p>
            <h2
              className="v40-h2 font-heading text-ds-h2 font-bold tracking-[-1.3px] leading-[1.08] text-navy-900 v40-reveal v40-d1"
              style={{ marginTop: 16, maxWidth: 900 }}
            >
              4 module aliniate la instrumentele oficiale DNSC pentru
              conformitate NIS2
            </h2>
            <div className="v40-mod-bands v40-reveal v40-d3">
              {modules.map((mod) => (
                <div key={mod.num} className="v40-mod-band">
                  <div className="v40-mod-step">
                    <span className="v40-mod-num font-heading text-4xl font-bold text-ds-green-500 leading-none">
                      {mod.num}
                    </span>
                    <span className="v40-mod-num-label text-ds-xxxs tracking-[1px] text-ds-text-500">
                      PAS
                    </span>
                  </div>
                  <div className="v40-mod-content">
                    <h3 className="v40-mod-title font-heading text-xl font-bold text-navy-900">
                      {mod.title}
                    </h3>
                    <p className="v40-mod-desc text-ds-xsm text-ds-text-500">
                      {mod.desc}
                    </p>
                    <p className="v40-mod-note text-ds-xxs text-ds-green-500">
                      {mod.note}
                    </p>
                  </div>
                  <div className="v40-mod-doc">
                    <span className="v40-mod-doc-label text-ds-xxxs font-bold tracking-[1.2px] text-ds-green-500">
                      DOCUMENT GENERAT
                    </span>
                    <h4 className="v40-mod-doc-title font-heading text-[15px] font-bold text-white">
                      {mod.docTitle}
                    </h4>
                    <p className="v40-mod-doc-desc text-ds-xxs text-ds-text-400">
                      {mod.docDesc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ====== SECTION 6: PRICING ====== */}
        <section
          className="v40-pricing v40-section-reveal"
          ref={setSectionRef(4)}
          id="v40-sec-pricing"
        >
          <div className="v40-inner">
            <p className="v40-eyebrow text-xs tracking-[1.4px] text-ds-blue-600 font-medium font-mono v40-reveal">
              PRETURI CONFORMITATE NIS2
            </p>
            <h2
              className="v40-h2 font-heading text-ds-h2 font-bold tracking-[-1.3px] leading-[1.08] text-navy-900 v40-reveal v40-d1"
              style={{ marginTop: 16 }}
            >
              Cat costa conformitatea NIS2 — Gratuit vs credite
            </h2>
            <p
              className="v40-body text-sm leading-[1.45] text-slate-700 v40-reveal v40-d2"
              style={{ marginTop: 16, maxWidth: 1000 }}
            >
              Platforma NIS2PASS ofera acces gratuit la toate fluxurile de
              evaluare si completare. Creditele sunt necesare doar pentru
              generarea documentelor oficiale DNSC.
            </p>
            <div className="v40-pricing-grid v40-reveal v40-d3">
              <div className="v40-pricing-free">
                <p
                  className="v40-pricing-title font-heading text-ds-xxl font-bold"
                  style={{ color: "#0F172A" }}
                >
                  Cont gratuit NIS2 · 100 credite incluse
                </p>
                <p className="v40-pricing-sub text-xs text-ds-text-500">
                  Evaluare completa fara costuri — parcurge toate fluxurile NIS2
                </p>
                <div
                  className="v40-pricing-divider"
                  style={{ background: "#E2E8F0" }}
                />
                <p className="v40-pricing-item font-heading text-ds-xsm font-semibold included">
                  <span className="v40-check">✓</span> Completare Formular de
                  Notificare
                </p>
                <p className="v40-pricing-item font-heading text-ds-xsm font-semibold included">
                  <span className="v40-check">✓</span> Completare Analiza Efect
                  Perturbator
                </p>
                <p className="v40-pricing-item font-heading text-ds-xsm font-semibold included">
                  <span className="v40-check">✓</span> Analiza Nivel de Risc
                  (ENIRE@RO)
                </p>
                <p className="v40-pricing-item font-heading text-ds-xsm font-semibold included">
                  <span className="v40-check">✓</span> Autoevaluare Maturitate
                  CyFun
                </p>
                <p className="v40-pricing-item font-heading text-ds-xsm font-semibold included">
                  <span className="v40-check">✓</span> Dashboard centralizat cu
                  rezultate
                </p>
                <p className="v40-pricing-item font-heading text-ds-xsm font-semibold excluded">
                  <span className="v40-cross">✕</span> Descarcare documente
                  PDF/Excel
                </p>
                <p className="v40-pricing-item font-heading text-ds-xsm font-semibold excluded">
                  <span className="v40-cross">✕</span> Autoevaluare asistata AI
                </p>
                <p className="v40-pricing-item font-heading text-ds-xsm font-semibold excluded">
                  <span className="v40-cross">✕</span> Generare Plan de
                  Remediere
                </p>
              </div>
              <div className="v40-pricing-paid">
                <p
                  className="v40-pricing-title font-heading text-ds-xxl font-bold"
                  style={{ color: "#FFF" }}
                >
                  Credite pentru documente oficiale NIS2
                </p>
                <p
                  className="v40-pricing-sub text-xs text-ds-text-500"
                  style={{ color: "#94A3B8" }}
                >
                  Descarca, transmite la DNSC, remediaza conform OUG 155/2024
                </p>
                <div
                  className="v40-pricing-divider"
                  style={{ background: "#1E293B" }}
                />
                <p className="v40-pricing-item font-heading text-ds-xsm font-semibold white">
                  ✓ Tot ce include contul gratuit
                </p>
                <p className="v40-pricing-item font-heading text-ds-xsm font-semibold white">
                  ✓ Descarcare Formular Notificare (PDF){" "}
                  <span className="v40-cr-badge text-ds-xxxs font-mono bg-ds-blue-700 text-white">
                    20 cr
                  </span>
                </p>
                <p className="v40-pricing-item font-heading text-ds-xsm font-semibold white">
                  ✓ Descarcare Raport Risc (PDF){" "}
                  <span className="v40-cr-badge text-ds-xxxs font-mono bg-ds-blue-700 text-white">
                    20 cr
                  </span>
                </p>
                <p className="v40-pricing-item font-heading text-ds-xsm font-semibold white">
                  ✓ Descarcare EVAL_MMS (Excel DNSC){" "}
                  <span className="v40-cr-badge text-ds-xxxs font-mono bg-ds-blue-700 text-white">
                    30 cr
                  </span>
                </p>
                <p className="v40-pricing-item font-heading text-ds-xsm font-semibold white">
                  ✓ Autoevaluare asistata consultant{" "}
                  <span className="v40-cr-badge text-ds-xxxs font-mono bg-ds-blue-700 text-white">
                    100 cr
                  </span>
                </p>
                <p className="v40-pricing-item font-heading text-ds-xsm font-semibold white">
                  ✓ Plan Remediere + Apetit Risc{" "}
                  <span className="v40-cr-badge text-ds-xxxs font-mono bg-ds-blue-700 text-white">
                    200 cr
                  </span>
                </p>
              </div>
            </div>
            <button
              type="button"
              className="v40-pricing-link v40-reveal v40-d4"
              onClick={() => navigate("/preturi")}
            >
              Vezi toate pachetele si optiunile de plata →
            </button>
          </div>
        </section>

        {/* ====== SECTION 7a: LEGAL FRAMEWORK ====== */}
        <section
          className="v40-legal v40-section-reveal"
          ref={setSectionRef(5)}
          id="v40-sec-legal"
        >
          <div className="v40-inner">
            <p className="v40-eyebrow text-xs tracking-[1.4px] text-ds-blue-600 font-medium font-mono v40-reveal">
              CADRU LEGAL NIS2 ROMANIA
            </p>
            <h2
              className="v40-h2 font-heading text-ds-h2 font-bold tracking-[-1.3px] leading-[1.08] text-navy-900 v40-reveal v40-d1"
              style={{ marginTop: 16, fontSize: 40, letterSpacing: -1.2 }}
            >
              Directiva (UE) 2022/2555 — NIS2 in legislatia Romaniei
            </h2>
            <p
              className="v40-body text-sm leading-[1.45] text-slate-700 v40-reveal v40-d2"
              style={{ marginTop: 16, maxWidth: 1000 }}
            >
              Directiva (UE) 2022/2555 privind securitatea cibernetica (NIS2) a
              fost transpusa in legislatia romana prin OUG nr. 155/2024 si
              completata de Legea nr. 124/2025.
            </p>
            <div
              className="v40-legal-cards v40-reveal v40-d3"
              ref={setStaggerRef(2)}
            >
              <div className="v40-legal-card v40-stagger-card">
                <span className="v40-legal-card-label text-ds-xxs tracking-[1.4px] text-ds-blue-800">
                  DIRECTIVA UE 2022/2555
                </span>
                <h3 className="v40-legal-card-title font-heading text-lg font-bold text-navy-900">
                  Directiva (UE) 2022/2555 — NIS2
                </h3>
                <p className="v40-legal-card-body text-xs text-ds-text-600">
                  Directiva europeana NIS2 stabileste standarde minime
                  armonizate de securitate cibernetica pentru entitatile
                  esentiale si importante din sectoarele critice ale UE.
                </p>
              </div>
              <div className="v40-legal-card v40-stagger-card">
                <span
                  className="v40-legal-card-label text-ds-xxs tracking-[1.4px] text-ds-blue-800"
                  style={{ color: "#B45309" }}
                >
                  OUG 155/2024
                </span>
                <h3 className="v40-legal-card-title font-heading text-lg font-bold text-navy-900">
                  OUG nr. 155/2024 — Transpunere NIS2
                </h3>
                <p className="v40-legal-card-body text-xs text-ds-text-600">
                  Aceasta ordonanta de urgenta transpune Directiva europeana
                  NIS2 in legislatia din Romania, instituind cadrul legal de
                  baza si cerintele obligatorii de securitate cibernetica pentru
                  organizatiile publice si private care activeaza in sectoare
                  critice sau importante.
                </p>
              </div>
              <div className="v40-legal-card v40-stagger-card">
                <span
                  className="v40-legal-card-label text-ds-xxs tracking-[1.4px] text-ds-blue-800"
                  style={{ color: "#047857" }}
                >
                  LEGEA NR. 124/2025
                </span>
                <h3 className="v40-legal-card-title font-heading text-lg font-bold text-navy-900">
                  Legea nr. 124/2025 — Aprobare si Completare NIS2
                </h3>
                <p className="v40-legal-card-body text-xs text-ds-text-600">
                  Acest act normativ aproba, oficializeaza si consolideaza
                  prevederile OUG 155/2024, aducand clarificari suplimentare
                  privind regimul sanctiunilor, responsabilizarea managementului
                  (inclusiv necesitatea desemnarii unui responsabil cu
                  securitatea) si protejarea confidentialitatii datelor
                  raportate.
                </p>
              </div>
              <div className="v40-legal-card v40-stagger-card">
                <span
                  className="v40-legal-card-label text-ds-xxs tracking-[1.4px] text-ds-blue-800"
                  style={{ color: "#7C3AED" }}
                >
                  ORDINE DNSC
                </span>
                <h3 className="v40-legal-card-title font-heading text-lg font-bold text-navy-900">
                  Ordinele DNSC pentru legea NIS
                </h3>
                <p className="v40-legal-card-body text-xs text-ds-text-600">
                  Aceste ordine reprezinta normele tehnice si practice de
                  aplicare a legislatiei primare, stabilind exact procedurile,
                  termenele si instrumentele (precum platforma NIS2@RO) prin
                  care companiile trebuie sa se notifice, sa se inregistreze si
                  sa isi realizeze autoevaluarile de risc cibernetic.
                </p>
                <p
                  className="v40-legal-card-body text-xs text-ds-text-600"
                  style={{ marginTop: 8, fontSize: 11, opacity: 0.7 }}
                >
                  10 Ordine DNSC (4 publicate)
                </p>
              </div>
            </div>
            <div className="v40-trust-banner v40-reveal v40-d4">
              <h3 className="v40-trust-title font-heading text-lg font-bold text-white">
                Relatia NIS2PASS cu DNSC si platforma oficiala NIS2@RO
              </h3>
              <p className="v40-trust-body text-xs text-ds-text-300">
                NIS2PASS este un instrument auxiliar de conformare cu Directiva
                2022/2555 si OUG 155/2024. Nu inlocuieste platforma oficiala
                NIS2@RO si nu ia decizii de clasificare — acestea apartin
                exclusiv DNSC ca autoritate competenta nationala.
              </p>
            </div>
            <div className="v40-badges v40-reveal v40-d5">
              <span className="v40-badge text-ds-xxs text-ds-blue-700 bg-ds-blue-50">
                ⚖ Aliniat cu OUG nr. 155/2024
              </span>
              <span className="v40-badge text-ds-xxs text-ds-blue-700 bg-ds-blue-50">
                📐 Metodologie oficiala DNSC / ENIRE@RO
              </span>
              <span className="v40-badge text-ds-xxs text-ds-blue-700 bg-ds-blue-50">
                🔒 Cadru CyFun — 6 functii de securitate
              </span>
              <span className="v40-badge text-ds-xxs text-ds-blue-700 bg-ds-blue-50">
                📄 Format oficial DNSC · NIS2@RO
              </span>
            </div>
          </div>
        </section>

        {/* ====== SECTION 7b: BENEFITS SLIDER ====== */}
        <section
          className="v40-benefits v40-section-reveal"
          ref={(el) => {
            setSectionRef(6)(el);
            benefitsRef.current = el;
          }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          tabIndex={-1}
        >
          <div className="v40-inner">
            <p
              className="v40-eyebrow text-xs tracking-[1.4px] text-ds-blue-600 font-medium font-mono v40-reveal"
              style={{ color: "#60A5FA" }}
            >
              BENEFICII PLATFORMA NIS2
            </p>
            <h2
              className="v40-h2 font-heading text-ds-h2 font-bold tracking-[-1.3px] leading-[1.08] text-navy-900 v40-reveal v40-d1"
              style={{ color: "#FFF", marginTop: 16 }}
            >
              Echipele de securitate cibernetica aleg NIS2PASS pentru
              conformitatea cu Directiva NIS2
            </h2>
          </div>
          <div
            className="v40-benefits-slider v40-reveal v40-d3"
            role="region"
            aria-label="Beneficii platforma"
            aria-roledescription="carusel"
          >
            <button
              type="button"
              className="v40-benefits-arrow left font-heading text-xl font-bold text-white"
              onClick={() => {
                pauseSliderAutoAdvance();
                setBenefitSlide(
                  (s) => (s - 1 + benefitCards.length) % benefitCards.length
                );
              }}
              aria-label="Beneficiul anterior"
            >
              ←
            </button>
            <div className="v40-benefit-slide">
              <div className="v40-benefit-slide-head">
                <span className="v40-benefit-num text-sm font-bold text-ds-blue-600">
                  {benefitCards[benefitSlide].num}
                </span>
                <span className="v40-benefit-tag text-ds-xxs font-medium text-ds-green-600 tracking-[0.5px]">
                  {benefitCards[benefitSlide].tag}
                </span>
              </div>
              <h3 className="v40-benefit-title font-heading text-[28px] font-bold text-navy-900">
                {benefitCards[benefitSlide].title}
              </h3>
              <p className="v40-benefit-body text-ds-xsm text-ds-text-600">
                {benefitCards[benefitSlide].body}
              </p>
              <div className="v40-benefit-stats">
                {benefitCards[benefitSlide].stats.map((s) => (
                  <div key={s.val} className="v40-benefit-stat">
                    <p className="v40-benefit-stat-val font-heading text-ds-xxl font-bold text-ds-blue-600">
                      {s.val}
                    </p>
                    <p className="v40-benefit-stat-label text-ds-xxs text-ds-text-500">
                      {s.label}
                    </p>
                  </div>
                ))}
              </div>
              <div className="v40-benefit-footer">
                <span className="v40-benefit-counter text-xs font-medium text-ds-border-700">
                  Beneficiul {benefitSlide + 1} din {benefitCards.length}
                </span>
                <span className="v40-benefit-hint text-ds-xxs text-ds-text-400">
                  &lt;- foloseste sagetile -&gt;
                </span>
              </div>
            </div>
            <button
              type="button"
              className="v40-benefits-arrow right font-heading text-xl font-bold text-white"
              onClick={() => {
                pauseSliderAutoAdvance();
                setBenefitSlide((s) => (s + 1) % benefitCards.length);
              }}
              aria-label="Beneficiul urmator"
            >
              →
            </button>
          </div>
          <div className="v40-benefit-dots v40-reveal v40-d4">
            {benefitCards.map((_, i) => (
              <span
                key={i}
                className={`v40-benefit-dot${i === benefitSlide ? " active" : ""}`}
              />
            ))}
            <button
              type="button"
              className="v40-slider-toggle"
              onClick={() =>
                setSliderPaused((p) => {
                  if (!p) {
                    if (sliderInteractionTimer.current)
                      clearTimeout(sliderInteractionTimer.current);
                  }
                  return !p;
                })
              }
              aria-label={
                sliderPaused
                  ? "Porneste avansarea automata"
                  : "Opreste avansarea automata"
              }
            >
              {sliderPaused ? (
                <svg
                  width="10"
                  height="12"
                  viewBox="0 0 10 12"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <polygon points="0,0 10,6 0,12" />
                </svg>
              ) : (
                <svg
                  width="10"
                  height="12"
                  viewBox="0 0 10 12"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <rect x="0" y="0" width="3" height="12" />
                  <rect x="7" y="0" width="3" height="12" />
                </svg>
              )}
            </button>
          </div>
        </section>

        {/* ====== SECTION 7c: PERSONAS ====== */}
        <section
          className="v40-personas v40-section-reveal"
          ref={setSectionRef(7)}
        >
          <div className="v40-inner">
            <p className="v40-eyebrow text-xs tracking-[1.4px] text-ds-blue-600 font-medium font-mono v40-reveal">
              PUBLIC TINTA NIS2
            </p>
            <h2
              className="v40-h2 font-heading text-ds-h2 font-bold tracking-[-1.3px] leading-[1.08] text-navy-900 v40-reveal v40-d1"
              style={{
                marginTop: 16,
                fontSize: 40,
                letterSpacing: -1.2,
                maxWidth: 900,
              }}
            >
              NIS2PASS — platforma pentru CISO, organizatii NIS2 si consultanti
              conformitate
            </h2>
            <div className="v40-persona-cards v40-reveal v40-d3">
              <div className="v40-persona-card">
                <h3 className="v40-persona-title font-heading text-lg font-bold text-navy-900">
                  🏢 Entitati esentiale si importante din sectoarele NIS2
                </h3>
                <p className="v40-persona-body text-ds-xxs text-ds-text-600">
                  Organizatii din cele 18 sectoare critice prevazute in Anexele
                  1-2 ale OUG 155/2024 care trebuie sa se notifice la DNSC, sa
                  evalueze riscurile conform ENIRE@RO si sa implementeze masuri
                  de securitate CyFun.
                </p>
              </div>
              <div className="v40-persona-card">
                <h3 className="v40-persona-title font-heading text-lg font-bold text-navy-900">
                  👤 CISO / Responsabil securitate cibernetica (art. 14 OUG 155)
                </h3>
                <p className="v40-persona-body text-ds-xxs text-ds-text-600">
                  Persoana desemnata conform art. 14 din OUG 155/2024 care
                  coordoneaza implementarea cerintelor NIS2, pregateste
                  documentatia DNSC si raporteaza stadiul conformitatii catre
                  conducere.
                </p>
              </div>
              <div className="v40-persona-card">
                <h3 className="v40-persona-title font-heading text-lg font-bold text-navy-900">
                  ⚖️ Consultanti NIS2 si firme de audit conformitate
                </h3>
                <p className="v40-persona-body text-ds-xxs text-ds-text-600">
                  Firme de consultanta specializate in Directiva 2022/2555,
                  auditori si DPO care asista organizatii multiple in procesul
                  de conformitate NIS2.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ====== SECTION 8a: FAQ ====== */}
        <section
          className="v40-faq v40-section-reveal"
          ref={setSectionRef(9)}
          id="v40-sec-faq"
        >
          <div className="v40-inner">
            <p className="v40-eyebrow text-xs tracking-[1.4px] text-ds-blue-600 font-medium font-mono v40-reveal">
              INTREBARI FRECVENTE NIS2
            </p>
            <h2
              className="v40-h2 font-heading text-ds-h2 font-bold tracking-[-1.3px] leading-[1.08] text-navy-900 v40-reveal v40-d1"
              style={{ marginTop: 16, fontSize: 40, letterSpacing: -1.2 }}
            >
              FAQ: Ce trebuie sa stii despre conformitatea NIS2 in Romania
            </h2>
            <div className="v40-faq-list v40-reveal v40-d3">
              {faqItems.map((faq, i) => (
                <div
                  key={faq.q}
                  className={`v40-faq-item${openFaq === i ? " v40-faq-open" : ""}`}
                >
                  <button
                    type="button"
                    className="v40-faq-trigger font-heading"
                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    aria-expanded={openFaq === i}
                  >
                    <span className="v40-faq-trigger-text text-xl font-bold text-navy-900">
                      {faq.q}
                    </span>
                    <span
                      style={{
                        transition: "transform 0.3s ease",
                        transform:
                          openFaq === i ? "rotate(180deg)" : "rotate(0deg)",
                        flexShrink: 0,
                      }}
                    >
                      <ChevronDown size={16} aria-hidden="true" />
                    </span>
                  </button>
                  <div
                    className={`v40-faq-answer-wrap${openFaq === i ? " open" : ""}`}
                  >
                    <div className="v40-faq-answer-inner">
                      <p className="v40-faq-answer text-xs text-ds-text-600">
                        {faq.a}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ====== SECTION 8b: ABOUT ====== */}
        <section
          className="v40-about v40-section-reveal"
          ref={setSectionRef(10)}
          id="v40-sec-about"
        >
          <div className="v40-inner">
            <p className="v40-eyebrow text-xs tracking-[1.4px] text-ds-blue-600 font-medium font-mono v40-reveal">
              DESPRE COMPLIANCE MADE EASY
            </p>
            <h2
              className="v40-h2 font-heading text-ds-h2 font-bold tracking-[-1.3px] leading-[1.08] text-navy-900 v40-reveal v40-d1"
              style={{ marginTop: 16, fontSize: 40, letterSpacing: -1.2 }}
            >
              Experti NIS2 Romania
            </h2>
            <div className="v40-about-body v40-reveal v40-d3">
              <div className="v40-about-left">
                <p className="v40-about-desc font-heading text-base text-navy-900">
                  NIS2PASS este dezvoltat si operat de Compliance Made Easy
                  S.R.L., companie specializata in securitate cibernetica,
                  conformitate NIS2 si governance GRC. Echipa reuneste experti
                  cu certificari internationale (CISA, CISSP, ISO 27001, CRISC),
                  auditori autorizati si specialisti cu experienta directa in
                  implementarea cerintelor NIS2.
                </p>
                <div className="v40-about-certs">
                  <span className="v40-about-cert text-ds-xxs text-ds-blue-700 bg-ds-blue-50">
                    CISA
                  </span>
                  <span className="v40-about-cert text-ds-xxs text-ds-blue-700 bg-ds-blue-50">
                    CISSP
                  </span>
                  <span className="v40-about-cert text-ds-xxs text-ds-blue-700 bg-ds-blue-50">
                    ISO 27001
                  </span>
                  <span className="v40-about-cert text-ds-xxs text-ds-blue-700 bg-ds-blue-50">
                    CRISC
                  </span>
                  <span className="v40-about-cert text-ds-xxs text-ds-blue-700 bg-ds-blue-50">
                    DPO
                  </span>
                  <span className="v40-about-cert text-ds-xxs text-ds-blue-700 bg-ds-blue-50">
                    CEH
                  </span>
                  <span className="v40-about-cert text-ds-xxs text-ds-blue-700 bg-ds-blue-50">
                    GDPR
                  </span>
                </div>
                <div className="v40-contact-form" style={{ marginTop: 28 }}>
                  <h3 className="v40-contact-form-title font-heading text-xl font-bold text-navy-900">
                    Intra in contact cu noi
                  </h3>
                  <div>
                    <label
                      htmlFor="v40-topic"
                      className="text-xs font-medium text-ds-border-700"
                    >
                      Subiect
                    </label>
                    <select
                      id="v40-topic"
                      value={contactTopic}
                      onChange={(e) => setContactTopic(e.target.value)}
                      style={
                        contactTopic ? { borderColor: "#10B981" } : undefined
                      }
                    >
                      <option value="">Selecteaza subiectul</option>
                      <option value="consultanta">
                        Consultanta directiva NIS2
                      </option>
                      <option value="implementare">
                        Implementare directiva NIS2
                      </option>
                      <option value="training">Training NIS2 si CyFUN</option>
                      <option value="alta">Alta optiune</option>
                    </select>
                    <span
                      className={`v40-select-feedback text-ds-xxs text-ds-green-600${contactTopic ? " visible" : ""}`}
                    >
                      <Check size={12} aria-hidden="true" /> Subiect selectat
                    </span>
                  </div>
                  {contactTopic === "alta" && (
                    <div>
                      <label
                        htmlFor="v40-alt-topic"
                        className="text-xs font-medium text-ds-border-700"
                      >
                        Descrie subiectul
                      </label>
                      <textarea
                        id="v40-alt-topic"
                        placeholder="Scrie aici subiectul dorit..."
                        rows={3}
                      />
                    </div>
                  )}
                  <div>
                    <label
                      htmlFor="v40-email"
                      className="text-xs font-medium text-ds-border-700"
                    >
                      Email
                    </label>
                    <input
                      className={`v40-cui-input v40-email-input font-mono text-sm text-navy-900${showEmailError ? " v40-email-error" : ""}${showEmailSuccess ? " v40-email-success" : ""}`}
                      id="v40-email"
                      type="email"
                      placeholder="adresa@companie.ro"
                      value={emailValue}
                      onChange={(e) => setEmailValue(e.target.value)}
                      onBlur={() => setEmailTouched(true)}
                      aria-invalid={showEmailError}
                      aria-describedby={
                        showEmailError ? "v40-email-err" : undefined
                      }
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        border: "1px solid #CBD5E1",
                        borderRadius: 8,
                        background: "#FFF",
                      }}
                    />
                    {showEmailError && (
                      <span
                        id="v40-email-err"
                        className="v40-email-feedback error text-ds-xxs"
                        role="alert"
                      >
                        Format email invalid
                      </span>
                    )}
                    {showEmailSuccess && (
                      <span className="v40-email-feedback success text-ds-xxs">
                        <Check size={12} aria-hidden="true" /> Email valid
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="v40-btn v40-btn-primary font-medium text-sm font-mono bg-ds-orange-500 text-[#111]"
                    onClick={requestDemo}
                  >
                    Trimite mesajul <ArrowRight size={16} aria-hidden="true" />
                  </button>
                </div>
              </div>
              <div className="v40-about-right">
                <div className="v40-about-svc">
                  <span className="v40-about-svc-label text-ds-xxxs text-ds-blue-800 tracking-[1.4px]">
                    SERVICIU PRINCIPAL
                  </span>
                  <h4 className="v40-about-svc-title font-heading text-base font-bold text-navy-900">
                    Consultanta NIS2 end-to-end Romania
                  </h4>
                  <p className="v40-about-svc-body text-ds-xxs text-ds-text-600">
                    De la evaluarea aplicabilitatii NIS2 pana la planul de
                    remediere si pregatirea pentru audit.
                  </p>
                </div>
                <div className="v40-about-svc">
                  <span className="v40-about-svc-label text-ds-xxxs text-ds-blue-800 tracking-[1.4px]">
                    EVALUARE SI AUDIT
                  </span>
                  <h4 className="v40-about-svc-title font-heading text-base font-bold text-navy-900">
                    Audit de securitate cibernetica NIS2
                  </h4>
                  <p className="v40-about-svc-body text-ds-xxs text-ds-text-600">
                    Evaluare independenta a conformitatii cu cerintele CyFun si
                    metodologia DNSC.
                  </p>
                </div>
                <div className="v40-about-svc">
                  <span className="v40-about-svc-label text-ds-xxxs text-ds-blue-800 tracking-[1.4px]">
                    ASISTENTA ENIRE@RO
                  </span>
                  <h4 className="v40-about-svc-title font-heading text-base font-bold text-navy-900">
                    Asistenta ENIRE@RO — scoruri impact
                  </h4>
                  <p className="v40-about-svc-body text-ds-xxs text-ds-text-600">
                    Elaborare justificari pentru modificarea valorilor
                    predefinite de impact si probabilitate.
                  </p>
                </div>
                <div className="v40-about-svc">
                  <span className="v40-about-svc-label text-ds-xxxs text-ds-blue-800 tracking-[1.4px]">
                    FORMARE PROFESIONALA
                  </span>
                  <h4 className="v40-about-svc-title font-heading text-base font-bold text-navy-900">
                    Training management NIS2
                  </h4>
                  <p className="v40-about-svc-body text-ds-xxs text-ds-text-600">
                    Formare profesionala conform art. 14 alin. (2) din OUG
                    155/2024.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ====== CYFUN CTA ====== */}
        <section
          className="v40-cyfun v40-section-reveal"
          ref={setSectionRef(11)}
        >
          <div className="v40-inner">
            <p
              className="v40-eyebrow text-xs tracking-[1.4px] text-ds-blue-600 font-medium font-mono v40-reveal"
              style={{ color: "#60A5FA", fontWeight: 500, textAlign: "center" }}
            >
              CADRUL DE SECURITATE CIBERNETICA
            </p>
            <h2
              className="v40-h2 font-heading text-ds-h2 font-bold tracking-[-1.3px] leading-[1.08] text-navy-900 v40-reveal v40-d1"
              style={{
                color: "#FFF",
                textAlign: "center",
                marginTop: 16,
                maxWidth: 900,
                margin: "16px auto 0",
              }}
            >
              CyberFundamentals (CyFun) — Cadrul Oficial DNSC
            </h2>
            <p
              className="v40-reveal v40-d2"
              style={{
                fontSize: 14,
                color: "#C7D2FE",
                lineHeight: 1.55,
                textAlign: "center",
                maxWidth: 800,
                margin: "16px auto 0",
              }}
            >
              Metodologia oficiala DNSC pentru evaluarea maturitatii securitatii
              cibernetice, adoptata din Belgia (CCB) si aliniata NIST CSF 2.0.
              CyFun structureaza 6 functii, 22 categorii si 218 cerinte pe 5
              niveluri de maturitate.
            </p>
            <div className="v40-reveal v40-d3" style={{ marginTop: 24 }}>
              <button
                type="button"
                className="v40-btn v40-btn-ghost"
                onClick={() => navigate("/cyfun-framework")}
              >
                Exploreaza cadrul CyFun complet{" "}
                <ArrowRight size={16} aria-hidden="true" />
              </button>
            </div>
          </div>
        </section>
      </main>

      <SharedFooter />

      {/* ====== BACK TO TOP ====== */}
      <button
        type="button"
        className={`v40-back-to-top${showBackToTop ? " visible" : ""}`}
        onClick={scrollToTop}
        aria-label="Inapoi sus"
      >
        <ChevronUp size={20} aria-hidden="true" />
      </button>

      {/* ====== MOBILE STICKY CTA ====== */}
      <div className={`v40-mobile-cta${showBackToTop ? " visible" : ""}`}>
        <button
          type="button"
          className="v40-btn v40-btn-primary v40-hero-cta"
          onClick={startEvaluation}
        >
          {isLoggedIn
            ? "Mergi la Companiile mele"
            : "Incepe conformarea — 100 credite gratuite"}{" "}
          <ArrowRight size={16} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default LandingAlternativeV40;
